import React, { useEffect, useState, useRef, useCallback } from "react";
import io from "socket.io-client";
import "./App.css";
import { sortScoreboardPlayers } from "./scoreboardOrder";

// Comments
//This line was for local hosting: const socket = io("http://localhost:3001");
//The line below is for actual hosting
const socket = io("https://api.riki-tiki.com"); //backend link here

const MAX_PLAYERS = 8;
const ROUND_SEQUENCE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
const HAND_ORDER_STORAGE_KEY = "rikiHandOrderIds";
const APA_MODE_STORAGE_KEY = "rikiApaMode";
const HIDE_CARDS_STORAGE_KEY = "rikiHideCards";
const PROFILE_STORAGE_KEY = "rikiFamilyProfileId";
const PROFILE_AUTH_STORAGE_KEY = "rikiFamilyProfileAuthToken";
const RECOVERY_KEY_STORAGE_KEY = "rikiRecoveryKey";
const SCOREBOARD_SCROLL_STORAGE_KEY = "rikiScoreboardScrollTop";
const HIDDEN_CARD_PATH = "/cards/back/hidden.png";
const IS_NATIVE_PHONE_APP = typeof window !== "undefined" && window.RIKI_TIKI_LOAD_NATIVE_PHONE_APP === true;
const FAMILY_PROFILES = [
  { id: "vitya", label: "Vitya", avatar: "/profiles/vitya.png" },
  { id: "matya", label: "Matya", avatar: "/profiles/matya.png" },
  { id: "laca", label: "Laca", avatar: "/profiles/laca.png" }
];

let apaNameMeasureContext = null;
const fitApaPlayedCardName = (name) => {
  const fullName = String(name || "");
  if (!apaNameMeasureContext) {
    apaNameMeasureContext = document.createElement("canvas").getContext("2d");
  }
  if (!apaNameMeasureContext) return { text: fullName, truncated: false };

  const fontFamily = '"Segoe UI", Roboto, Helvetica, Arial, sans-serif';
  const fontSize = IS_NATIVE_PHONE_APP ? 12 : 18;
  const maxWidth = IS_NATIVE_PHONE_APP ? 42 : 64;
  apaNameMeasureContext.font = `800 ${fontSize}px ${fontFamily}`;
  if (apaNameMeasureContext.measureText(fullName).width <= maxWidth - 2) {
    return { text: fullName, truncated: false };
  }

  apaNameMeasureContext.font = `600 ${IS_NATIVE_PHONE_APP ? 10 : 14}px ${fontFamily}`;
  const dotsWidth = apaNameMeasureContext.measureText("…").width - 2;
  apaNameMeasureContext.font = `800 ${fontSize}px ${fontFamily}`;
  let text = "";
  for (const character of Array.from(fullName)) {
    if (apaNameMeasureContext.measureText(text + character).width + dotsWidth > maxWidth - 2) break;
    text += character;
  }
  return { text, truncated: true };
};

const getOrdinal = (n) => {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

const VALUE_ORDER_MAP = { 
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 
  'J': 11, 'Q': 12, 'K': 13, 'A': 14 
};

const sortHandVisual = (hand, trumpSuit) => {
  if (!hand || hand.length === 0) return [];
  
  // Create a copy with original indices attached
  const handWithIndices = hand.map((card, originalIndex) => ({ ...card, originalIndex }));

  if (!trumpSuit) {
    const basicOrder = { 'S': 0, 'H': 1, 'C': 2, 'D': 3 };
    return handWithIndices.sort((a, b) => 
      basicOrder[a.suit] - basicOrder[b.suit] || VALUE_ORDER_MAP[b.value] - VALUE_ORDER_MAP[a.value]
    );
  }

  const isRed = (s) => s === 'H' || s === 'D';
  const trumpIsRed = isRed(trumpSuit);

  const counts = hand.reduce((acc, card) => {
    acc[card.suit] = (acc[card.suit] || 0) + 1;
    return acc;
  }, {});

  const opposites = trumpIsRed ? ['S', 'C'] : ['H', 'D'];
  opposites.sort((a, b) => (counts[b] || 0) - (counts[a] || 0));

  const otherSameColor = trumpIsRed 
    ? (trumpSuit === 'D' ? 'H' : 'D') 
    : (trumpSuit === 'S' ? 'C' : 'S');

  // Hierarchy: Trump -> Top Opp -> Same Color (H) -> Last Opp (C)
  const suitPriority = {
    [trumpSuit]: 0,
    [opposites[0]]: 1,
    [otherSameColor]: 2,
    [opposites[1]]: 3
  };

  return handWithIndices.sort((a, b) => {
    if (a.suit !== b.suit) return suitPriority[a.suit] - suitPriority[b.suit];
    return VALUE_ORDER_MAP[b.value] - VALUE_ORDER_MAP[a.value];
  });
};

function App() {
  const [bootReady, setBootReady] = useState(false);
  const [canReload, setCanReload] = useState(false);
  const [winnerData, setWinnerData] = useState(null);
  const [finalScoreboardData, setFinalScoreboardData] = useState(null);
  const [players, setPlayers] = useState(Array(MAX_PLAYERS).fill(null));
  const [yourName, setYourName] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState(() => {
    try {
      return localStorage.getItem(PROFILE_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [showProfileChoices, setShowProfileChoices] = useState(false);
  const [passwordProfileId, setPasswordProfileId] = useState(null);
  const [profilePassword, setProfilePassword] = useState("");
  const [profilePasswordError, setProfilePasswordError] = useState("");
  const [profilePasswordRequired, setProfilePasswordRequired] = useState(true);
  const profilePasswordInputRef = useRef(null);
  const pendingProfileLoginRef = useRef(null);
  const profileLoginSequenceRef = useRef(0);
  const cancelPendingProfileLogin = useCallback(() => {
    const pending = pendingProfileLoginRef.current;
    pendingProfileLoginRef.current = null;
    if (!pending) return;
    if (Array.isArray(socket.sendBuffer)) {
      socket.sendBuffer = socket.sendBuffer.filter(packet => !(
        ["profileLoginAttempt", "profileLoginConfirm"].includes(packet.data?.[0]) &&
        packet.data?.[1]?.requestId === pending.requestId
      ));
    }
    if (socket.connected) socket.emit("profileLoginCancel", { requestId: pending.requestId });
  }, []);
  const requestProfileLogin = (profileId, password) => {
    cancelPendingProfileLogin();
    const request = {
      profileId,
      requestId: `${Date.now()}-${++profileLoginSequenceRef.current}`,
      ...(password === undefined ? {} : { password })
    };
    pendingProfileLoginRef.current = request;
    if (socket.connected) socket.emit("profileLoginAttempt", request);
  };
  useEffect(() => {
    if (!profileModalOpen) {
      cancelPendingProfileLogin();
      setPasswordProfileId(null);
      setProfilePassword("");
      setProfilePasswordError("");
    }
  }, [profileModalOpen, cancelPendingProfileLogin]);
  useEffect(() => {
    if (profileModalOpen && passwordProfileId) profilePasswordInputRef.current?.focus();
  }, [profileModalOpen, passwordProfileId, profilePasswordError]);
  const [takeoverMessage, setTakeoverMessage] = useState(null);
  const [startErrorMessage, setStartErrorMessage] = useState("");
  const [fillSeatsPopupOpen, setFillSeatsPopupOpen] = useState(false);
  const [gameTerminationMessage, setGameTerminationMessage] = useState("");
  const [seatExpired, setSeatExpired] = useState(false);
  const initialToken = (() => {
  try { return localStorage.getItem("rikiReconnectToken"); } catch { return null; }
  })();

  const [seated, setSeated] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(!!initialToken);
  const [hasCheckedReconnect, setHasCheckedReconnect] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(() =>
    typeof navigator !== "undefined" && !navigator.onLine ? "offline" : null
  );
  const [connectionStalled, setConnectionStalled] = useState(false);
  const connectionRecoveryRef = useRef({
    active: typeof navigator !== "undefined" && !navigator.onLine,
    disconnected: false,
    seatConfirmed: false,
    stateReceived: false
  });
  const suppressConnectionStatusRef = useRef(false);
  const connectionRecoveryTimeoutRef = useRef(null);
  const [gameState, setGameState] = useState({ 
    gameStarted: false, 
    currentRound: 1, 
    callerIndex: -1,
    turnIndex: -1, 
    totalBids: 0, 
    trumpCard: null, 
    playedCards: [], 
    bidsComplete: false 
  });
  const gameStartedRef = useRef(false);
  const hasReceivedGameStateRef = useRef(false);
  const lobbySeatWasLostRef = useRef(false);
  const ownLobbyPlayerIdRef = useRef(null);
  const [selectedBid, setSelectedBid] = useState(null);
  const [bidConfirmationOpen, setBidConfirmationOpen] = useState(false);
  const [scoreHistory, setScoreHistory] = useState({});
  const [isPeeking, setIsPeeking] = useState(false);
  const [isTricksPeeking, setIsTricksPeeking] = useState(false);
  const [isViewingTricks, setIsViewingTricks] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [draggingCard, setDraggingCard] = useState(null);
  const [postDragHoverCardId, setPostDragHoverCardId] = useState(null);
  const [reorderPreview, setReorderPreview] = useState(null);
  const [suppressHandTransition, setSuppressHandTransition] = useState(false);
  const [manualHandOrderIds, setManualHandOrderIds] = useState(() => {
    try {
      const saved = localStorage.getItem(HAND_ORDER_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [playFlight, setPlayFlight] = useState(null);
  const [apaMode, setApaMode] = useState(() => {
    try {
      return localStorage.getItem(APA_MODE_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }); // Apa helper
  const [hideCards, setHideCards] = useState(() => {
    try {
      return localStorage.getItem(HIDE_CARDS_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [dcTimers, setDcTimers] = useState({});
  
  const columnRef = useRef(null);
  const scoreboardScrollRef = useRef(null);
  const scoreboardScrollRestoredRef = useRef(false);
  const scoreboardScrollSaveTimerRef = useRef(null);
  const scoreboardStickToBottomRef = useRef(false);
  const endScoreboardScrollRef = useRef(null);
  const [colWidth, setColWidth] = useState(0);
  const playAreaRef = useRef(null);
  const handContainerRef = useRef(null);
  const lastSortedHandRef = useRef((Array.isArray(manualHandOrderIds) ? manualHandOrderIds : []).map(id => ({ id })));
  const draggingCardRef = useRef(null);
  const reorderPreviewRef = useRef(null);
  const dragSessionRef = useRef(0);
  const isCardLegalRef = useRef(null);
  const latestDragContextRef = useRef({
    isMyTurn: false,
    me: null,
    playedCards: []
  });

  useEffect(() => {
    if (!IS_NATIVE_PHONE_APP || !window.RikiTikiLoading) return;
    const frameId = requestAnimationFrame(() => window.RikiTikiLoading.ready());
    return () => cancelAnimationFrame(frameId);
  }, []);

  useEffect(() => {
    const measure = () => {
      if (columnRef.current) {
        setColWidth(columnRef.current.offsetWidth);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [players]); 

  useEffect(() => {
    try {
      localStorage.setItem(APA_MODE_STORAGE_KEY, apaMode ? "true" : "false");
    } catch {
      // Ignore storage errors so the helper still works normally.
    }
  }, [apaMode]);

  useEffect(() => {
    try {
      localStorage.setItem(HIDE_CARDS_STORAGE_KEY, hideCards ? "true" : "false");
    } catch {
      // Ignore storage errors so the toggle still works normally.
    }
  }, [hideCards]);

  useEffect(() => {
    if (IS_NATIVE_PHONE_APP || !bootReady || scoreboardScrollRestoredRef.current) return;

    let resizeObserver = null;
    let restoreFrameId = null;
    const restoreTimerId = setTimeout(() => {
      const wrapper = scoreboardScrollRef.current;
      if (!wrapper) return;

      try {
        const storedPosition = localStorage.getItem(SCOREBOARD_SCROLL_STORAGE_KEY);
        if (storedPosition === "bottom") {
          scoreboardStickToBottomRef.current = true;
          const pinToBottom = () => {
            if (scoreboardStickToBottomRef.current) {
              wrapper.scrollTop = wrapper.scrollHeight;
            }
          };

          pinToBottom();
          restoreFrameId = requestAnimationFrame(pinToBottom);

          if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(pinToBottom);
            resizeObserver.observe(wrapper);
            const table = wrapper.querySelector(".score-table");
            if (table) resizeObserver.observe(table);
          }
        } else {
          scoreboardStickToBottomRef.current = false;
          const savedScrollTop = Number(storedPosition);
          if (Number.isFinite(savedScrollTop)) wrapper.scrollTop = savedScrollTop;
        }
      } catch {
        // Ignore storage errors and leave the scoreboard at the top.
      }

      scoreboardScrollRestoredRef.current = true;
    }, 0);

    return () => {
      clearTimeout(restoreTimerId);
      if (restoreFrameId !== null) cancelAnimationFrame(restoreFrameId);
      if (resizeObserver) resizeObserver.disconnect();
      if (scoreboardScrollSaveTimerRef.current) {
        clearTimeout(scoreboardScrollSaveTimerRef.current);
      }
    };
  }, [bootReady]);

  useEffect(() => {
    if (!winnerData || !finalScoreboardData) return;

    let secondFrameId = null;
    const firstFrameId = requestAnimationFrame(() => {
      const wrapper = endScoreboardScrollRef.current;
      if (!wrapper) return;

      wrapper.scrollTop = wrapper.scrollHeight;
      secondFrameId = requestAnimationFrame(() => {
        wrapper.scrollTop = wrapper.scrollHeight;
      });
    });

    return () => {
      cancelAnimationFrame(firstFrameId);
      if (secondFrameId !== null) cancelAnimationFrame(secondFrameId);
    };
  }, [winnerData, finalScoreboardData]);

  const getVisualSeat = (actualIndex) => {
    const mySeat = players.findIndex(p => p && p.id === socket.id);
    if (mySeat === -1) return actualIndex + 1; 
    if (!gameState.gameStarted) {
      let visualPos = (actualIndex - mySeat + 4 + MAX_PLAYERS) % MAX_PLAYERS;
      return visualPos + 1;
    }
    if (actualIndex === mySeat) return 5;

    const opponentsInTableOrder = [];
    for (let offset = 1; offset < MAX_PLAYERS; offset++) {
      const index = (mySeat - offset + MAX_PLAYERS) % MAX_PLAYERS;
      if (players[index]) opponentsInTableOrder.push(index);
    }

    const opponentPosition = opponentsInTableOrder.indexOf(actualIndex);
    const rightCount = Math.ceil(opponentsInTableOrder.length / 2);
    if (opponentPosition < rightCount) return 4 - opponentPosition;

    const leftCount = opponentsInTableOrder.length - rightCount;
    return 5 + leftCount - (opponentPosition - rightCount);
  };

  const getSvgPath = (imgName) => {
    if (!imgName) return "";
    const baseName = imgName.split('.')[0];
    return `/cards/${baseName}.png`;
  };

  const getBackPath = (color) => {
    const fileName = color === 'red' ? 'CardRed.svg' : 'CardBlue.svg';
    return `/cards/back/${fileName}`;
  };

  const getSimpleBackPath = (color) => {
    const fileName = color === 'red' ? 'CardRedSimple.svg' : 'CardBlueSimple.svg';
    return `/cards/back/${fileName}`;
  };

  // HELPER: Check if a card is legal to play
  const isCardLegal = useCallback((card, hand) => {
    if (!gameState.playedCards || gameState.playedCards.length === 0) return true;
    
    const leadSuit = gameState.playedCards[0].card.suit;
    const trumpSuit = gameState.trumpCard?.suit;
    
    const hasLeadSuit = hand.some(c => c.suit === leadSuit);
    const hasTrumpSuit = hand.some(c => c.suit === trumpSuit);

    if (hasLeadSuit) {
      return card.suit === leadSuit;
    } else if (hasTrumpSuit) {
      return card.suit === trumpSuit;
    }
    
    return true; // Can play anything if no lead and no trump
  }, [gameState.playedCards, gameState.trumpCard]);

  const activeProfile = FAMILY_PROFILES.find(profile => profile.id === selectedProfileId) || null;

  useEffect(() => {
    if (gameState.gameStarted && !activeProfile && profileModalOpen) {
      cancelPendingProfileLogin();
      setProfileModalOpen(false);
      setShowProfileChoices(false);
    }
  }, [gameState.gameStarted, activeProfile, profileModalOpen, cancelPendingProfileLogin]);

  const chooseProfile = (profileId) => {
    const profile = FAMILY_PROFILES.find(item => item.id === profileId);
    if (!profile) return;

    if (!profilePasswordRequired || gameState.debug) {
      requestProfileLogin(profile.id);
    } else {
      setPasswordProfileId(profile.id);
      setProfilePassword("");
      setProfilePasswordError("");
    }
  };

  const logoutProfile = () => {
    if (gameState.gameStarted) return;
    cancelPendingProfileLogin();
    localStorage.removeItem(PROFILE_STORAGE_KEY);
    localStorage.removeItem(PROFILE_AUTH_STORAGE_KEY);
    setSelectedProfileId("");
    setProfileModalOpen(false);
    setShowProfileChoices(false);
    socket.emit("profileLogout");
  };

  const submitSeatRequest = () => {
    if (!yourName) return;
    setStartErrorMessage("");
    socket.emit("takeSeat", {
      name: yourName,
      profileId: activeProfile?.id || null,
      authToken: localStorage.getItem(PROFILE_AUTH_STORAGE_KEY)
    });
  };

  useEffect(() => {
  const interval = setInterval(() => {
    setDcTimers(prev => {
      const updated = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (v > 0) updated[k] = v - 1;
      });
      return updated;
    });
  }, 1000);

  return () => clearInterval(interval);
}, []);

  useEffect(() => {
    // --- AUTO RECONNECT ON LOAD ---
const clearConnectionRecoveryTimeout = () => {
  if (connectionRecoveryTimeoutRef.current) {
    clearTimeout(connectionRecoveryTimeoutRef.current);
    connectionRecoveryTimeoutRef.current = null;
  }
};

const clearLobbySeat = () => {
  lobbySeatWasLostRef.current = true;
  const ownPlayerId = ownLobbyPlayerIdRef.current;
  if (ownPlayerId) {
    setPlayers(current => current.map(player => player?.playerId === ownPlayerId ? null : player));
    ownLobbyPlayerIdRef.current = null;
  }
  setSeated(false);
  setIsReconnecting(false);
  setBootReady(true);
  localStorage.removeItem("rikiReconnectToken");
  localStorage.removeItem("rikiWasSeated");
  localStorage.removeItem(RECOVERY_KEY_STORAGE_KEY);
};
const isPreGameConnection = () =>
  !gameStartedRef.current &&
  (hasReceivedGameStateRef.current || localStorage.getItem("rikiWasSeated") !== "1");

const maybeCompleteConnectionRecovery = () => {
  const recovery = connectionRecoveryRef.current;
  if (
    recovery.active &&
    recovery.seatConfirmed &&
    recovery.stateReceived &&
    socket.connected &&
    navigator.onLine
  ) {
    if (IS_NATIVE_PHONE_APP && recovery.phoneReturn) {
      if (recovery.phoneCompletionScheduled) return;
      recovery.phoneCompletionScheduled = true;
      clearConnectionRecoveryTimeout();
      const showRejoined = () => {
        if (connectionRecoveryRef.current !== recovery || !socket.connected || !navigator.onLine) return;
        setConnectionStatus("rejoining");
        connectionRecoveryTimeoutRef.current = setTimeout(() => {
          if (connectionRecoveryRef.current !== recovery) return;
          recovery.phoneReturn = false;
          maybeCompleteConnectionRecovery();
        }, 650);
      };
      const blueTimeLeft = Math.max(0, 650 - (Date.now() - recovery.phoneBlueStartedAt));
      if (blueTimeLeft) {
        connectionRecoveryTimeoutRef.current = setTimeout(showRejoined, blueTimeLeft);
      } else {
        showRejoined();
      }
      return;
    }
    clearConnectionRecoveryTimeout();
    connectionRecoveryRef.current = { active: false, disconnected: false, seatConfirmed: false, stateReceived: false };
    setConnectionStatus(null);
  }
};

let observedEngine = null;
let heartbeatWarningTimer = null;
const clearHeartbeatWarning = () => {
  if (heartbeatWarningTimer) clearTimeout(heartbeatWarningTimer);
  heartbeatWarningTimer = null;
  setConnectionStalled(false);
};
const scheduleHeartbeatWarning = () => {
  if (heartbeatWarningTimer) clearTimeout(heartbeatWarningTimer);
  heartbeatWarningTimer = setTimeout(() => {
    heartbeatWarningTimer = null;
    if (
      socket.connected && navigator.onLine &&
      document.visibilityState === "visible" &&
      gameStartedRef.current &&
      !connectionRecoveryRef.current.active &&
      !suppressConnectionStatusRef.current &&
      localStorage.getItem("rikiWasSeated") === "1"
    ) {
      setConnectionStalled(true);
    }
  }, 8000);
};
const onEnginePing = () => {
  clearHeartbeatWarning();
  scheduleHeartbeatWarning();
  const recovery = connectionRecoveryRef.current;
  if (recovery.active && !recovery.disconnected && navigator.onLine) {
    recovery.active = false;
    setConnectionStatus(null);
  }
};
const watchEngineHeartbeat = () => {
  const engine = socket.io.engine;
  if (!engine || engine === observedEngine) return;
  if (observedEngine) observedEngine.off("ping", onEnginePing);
  observedEngine = engine;
  observedEngine.on("ping", onEnginePing);
};
watchEngineHeartbeat();
if (socket.connected) scheduleHeartbeatWarning();

const onBrowserOffline = () => {
  if (suppressConnectionStatusRef.current) return;
  clearHeartbeatWarning();
  if (IS_NATIVE_PHONE_APP) document.activeElement?.blur?.();
  if (IS_NATIVE_PHONE_APP && connectionRecoveryRef.current.phoneCompletionScheduled) {
    clearConnectionRecoveryTimeout();
    connectionRecoveryRef.current.phoneCompletionScheduled = false;
  }
  connectionRecoveryRef.current.active = true;
  connectionRecoveryRef.current.wasOffline = true;
  setConnectionStatus("offline");
};

const onBrowserOnline = () => {
  if (suppressConnectionStatusRef.current) return;
  if (isPreGameConnection()) {
    clearConnectionRecoveryTimeout();
    connectionRecoveryRef.current = { active: false, disconnected: false, seatConfirmed: false, stateReceived: false };
    setConnectionStatus(null);
    return;
  }
  const recovery = connectionRecoveryRef.current;
  if (IS_NATIVE_PHONE_APP && recovery.wasOffline) {
    if (socket.connected && !recovery.disconnected) {
      recovery.active = false;
      recovery.wasOffline = false;
      recovery.phoneReturn = false;
      setConnectionStatus(null);
      scheduleHeartbeatWarning();
    } else {
      recovery.active = true;
      recovery.phoneReturn = true;
      recovery.phoneBlueStartedAt = Date.now();
      setConnectionStatus("restoring");
      if (recovery.seatConfirmed && recovery.stateReceived) {
        maybeCompleteConnectionRecovery();
      }
    }
    return;
  }
  if (socket.connected && !recovery.disconnected) {
    recovery.active = true;
    setConnectionStatus("checking");
  } else if (socket.connected) {
    setConnectionStatus("rejoining");
    maybeCompleteConnectionRecovery();
  } else {
    recovery.active = true;
    setConnectionStatus("restoring");
  }
};

const onSocketDisconnect = () => {
  if (suppressConnectionStatusRef.current) return;
  clearHeartbeatWarning();
  clearConnectionRecoveryTimeout();
  if (isPreGameConnection()) {
    const wasOffline = connectionRecoveryRef.current.wasOffline || !navigator.onLine;
    clearLobbySeat();
    connectionRecoveryRef.current = { active: false, disconnected: false, seatConfirmed: false, stateReceived: false, wasOffline };
    setConnectionStatus(wasOffline ? "offline" : null);
    return;
  }
  connectionRecoveryRef.current = {
    active: true,
    disconnected: true,
    seatConfirmed: false,
    stateReceived: false,
    wasOffline: connectionRecoveryRef.current.wasOffline,
    phoneReturn: connectionRecoveryRef.current.phoneReturn,
    phoneBlueStartedAt: Date.now()
  };
  setConnectionStatus(navigator.onLine
    ? (connectionRecoveryRef.current.wasOffline ? "restoring" : "disconnected")
    : "offline");
};

const onConnectError = () => {
  if (suppressConnectionStatusRef.current) return;
  clearHeartbeatWarning();
  if (isPreGameConnection()) {
    setConnectionStatus(connectionRecoveryRef.current.wasOffline || !navigator.onLine ? "offline" : null);
    return;
  }
  if (connectionRecoveryRef.current.active || localStorage.getItem("rikiWasSeated") === "1") {
    connectionRecoveryRef.current.active = true;
    setConnectionStatus(navigator.onLine
      ? (connectionRecoveryRef.current.wasOffline ? "restoring" : "disconnected")
      : "offline");
  }
};

window.addEventListener("offline", onBrowserOffline);
window.addEventListener("online", onBrowserOnline);
socket.on("disconnect", onSocketDisconnect);
socket.on("connect_error", onConnectError);

socket.on("reconnectFailed", () => {
  setIsReconnecting(false);
  if (!gameStartedRef.current) {
    clearLobbySeat();
    setConnectionStatus(navigator.onLine ? null : "offline");
    return;
  }
  if (connectionRecoveryRef.current.active) {
    clearConnectionRecoveryTimeout();
    connectionRecoveryRef.current.disconnected = true;
    setConnectionStatus("failed");
  }
});

socket.on("connect", () => {
  watchEngineHeartbeat();
  clearHeartbeatWarning();
  scheduleHeartbeatWarning();
  const storedToken = localStorage.getItem("rikiReconnectToken");
  const wasSeated = localStorage.getItem("rikiWasSeated") === "1";
  const storedProfileId = localStorage.getItem(PROFILE_STORAGE_KEY) || "";

  if (pendingProfileLoginRef.current) {
    socket.emit("profileLoginAttempt", pendingProfileLoginRef.current);
    setIsReconnecting(false);
    setBootReady(true);
    return;
  }

  if (isPreGameConnection() && navigator.onLine) {
    connectionRecoveryRef.current.active = false;
    setConnectionStatus(null);
  }

  if (lobbySeatWasLostRef.current) {
    setIsReconnecting(false);
    setBootReady(true);
    return;
  }

  if (connectionRecoveryRef.current.active) {
    if (wasSeated) {
      connectionRecoveryRef.current.disconnected = true;
      connectionRecoveryRef.current.seatConfirmed = false;
      connectionRecoveryRef.current.stateReceived = false;
      setConnectionStatus(navigator.onLine ? (connectionRecoveryRef.current.phoneReturn ? "restoring" : "rejoining") : "offline");
      clearConnectionRecoveryTimeout();
      connectionRecoveryTimeoutRef.current = setTimeout(() => {
        if (connectionRecoveryRef.current.active && socket.connected) {
          setConnectionStatus("failed");
        }
      }, 8000);
    } else if (navigator.onLine) {
      connectionRecoveryRef.current.active = false;
      setConnectionStatus(null);
    }
  }

  if (storedProfileId) {
    setSelectedProfileId(storedProfileId);
    setIsReconnecting(true);
    socket.emit("profileLogin", {
      profileId: storedProfileId, reconnectToken: storedToken,
      authToken: localStorage.getItem(PROFILE_AUTH_STORAGE_KEY)
    });

    setTimeout(() => {
      setIsReconnecting(false);
      setBootReady(true);
    }, 600);
    return;
  }

  // If we were never seated, show popup instantly (no reconnect waiting)
  if (!storedToken || !wasSeated) {
    setIsReconnecting(false);
    setBootReady(true);
    return;
  }

  // We WERE seated before -> attempt reconnect
  setIsReconnecting(true);
  socket.emit("reconnectRequest", { reconnectToken: storedToken, recoveryKey: localStorage.getItem(RECOVERY_KEY_STORAGE_KEY) });

  // Don’t “hang” forever if reconnect fails
  setTimeout(() => {
    setIsReconnecting(false);
    setBootReady(true);
  }, 600); /* It was 600 I just lowever for debugging */
});
    socket.on("playersUpdate", (data) => {
  const ownPlayer = data.find(player => player && player.id === socket.id);
  if (ownPlayer) {
    ownLobbyPlayerIdRef.current = ownPlayer.playerId;
    lobbySeatWasLostRef.current = false;
    setSeated(true);
    setIsReconnecting(false);
    setBootReady(true);
  }
  setPlayers(data);

    const newTimers = {};
  data.forEach(p => {
    if (p && p.disconnected && p.reconnectTimeLeft !== null) {
      newTimers[p.id || p.reconnectToken] = p.reconnectTimeLeft;
    }
  });

  setDcTimers(newTimers);

  // reset cached hand order after reconnect
  

  const storedToken = localStorage.getItem("rikiReconnectToken");

  const stillSeated = data.some(
    p => p && (p.id === socket.id || p.reconnectToken === storedToken)
  );

  // If server no longer recognizes you as seated, clear reconnect flags
  if (!stillSeated && seated && !isReconnecting) {
    setSeated(false);
    localStorage.removeItem("rikiReconnectToken");
    localStorage.removeItem("rikiWasSeated");
    localStorage.removeItem(RECOVERY_KEY_STORAGE_KEY);
  }
});

socket.on("seatConfirmed", ({ reconnectToken, recoveryKey }) => {
  lobbySeatWasLostRef.current = false;

  // Keep the established hand order when confirming a reconnect.
  setStartErrorMessage("");

  try {
    if (reconnectToken) {
      localStorage.setItem("rikiReconnectToken", reconnectToken);
      localStorage.setItem("rikiWasSeated", "1");
    }
    if (recoveryKey) localStorage.setItem(RECOVERY_KEY_STORAGE_KEY, recoveryKey);
  } catch {
    // A browser storage failure must not leave a confirmed player in the seat popup.
  }

  setSeated(true);
  setIsReconnecting(false);
  setBootReady(true);
  suppressConnectionStatusRef.current = false;
  if (connectionRecoveryRef.current.phoneReturn) {
    connectionRecoveryRef.current.stateReceived = false;
  }
  connectionRecoveryRef.current.seatConfirmed = true;
  maybeCompleteConnectionRecovery();
});

const onSeatTakenOver = (kind, profileLabel, acknowledge) => {
  clearConnectionRecoveryTimeout();
  clearHeartbeatWarning();
  suppressConnectionStatusRef.current = true;
  setConnectionStatus(null);
  setSeated(false);
  setIsReconnecting(false);
  setBootReady(true);
  setTakeoverMessage({ kind, profileLabel });
  acknowledge?.();
};

socket.on("profileTakenOver", ({ profileLabel } = {}, acknowledge) => {
  onSeatTakenOver("profile", profileLabel || "This profile", acknowledge);
});
socket.on("guestSeatTakenOver", (_payload, acknowledge) => {
  onSeatTakenOver("guest", null, acknowledge);
});

socket.on("profileAuthConfig", ({ passwordRequired }) => {
  setProfilePasswordRequired(passwordRequired);
});

socket.on("profileLoginReady", ({ profileId, requestId }) => {
  const pending = pendingProfileLoginRef.current;
  if (pending?.requestId !== requestId || pending.profileId !== profileId) return;
  if (socket.connected) socket.emit("profileLoginConfirm", { requestId });
});

socket.on("profileAuthenticated", ({ profileId, authToken, requestId }) => {
  if (requestId && pendingProfileLoginRef.current?.requestId !== requestId) return;
  if (requestId) pendingProfileLoginRef.current = null;
  localStorage.setItem(PROFILE_STORAGE_KEY, profileId);
  localStorage.setItem(PROFILE_AUTH_STORAGE_KEY, authToken);
  setSelectedProfileId(profileId);
  setPasswordProfileId(null);
  setProfilePassword("");
  setProfilePasswordError("");
  setProfileModalOpen(false);
  setShowProfileChoices(false);
  setTakeoverMessage(null);
  lobbySeatWasLostRef.current = false;
});

socket.on("profilePasswordRequired", ({ profileId, message, requestId }) => {
  if (requestId && pendingProfileLoginRef.current?.requestId !== requestId) return;
  if (requestId) pendingProfileLoginRef.current = null;
  if (!FAMILY_PROFILES.some(profile => profile.id === profileId)) return;
  clearConnectionRecoveryTimeout();
  connectionRecoveryRef.current.active = false;
  setConnectionStatus(null);
  if (localStorage.getItem(PROFILE_STORAGE_KEY) === profileId) {
    localStorage.removeItem(PROFILE_STORAGE_KEY);
    localStorage.removeItem(PROFILE_AUTH_STORAGE_KEY);
    setSelectedProfileId("");
  }
  setPasswordProfileId(profileId);
  setProfilePassword("");
  setProfilePasswordError(message || "");
  setShowProfileChoices(true);
  setProfileModalOpen(true);
  setIsReconnecting(false);
  setBootReady(true);
});

socket.on("profileLoginRejected", (msg) => {
  alert(msg);
  setIsReconnecting(false);
  setBootReady(true);
  if (connectionRecoveryRef.current.active) {
    clearConnectionRecoveryTimeout();
    connectionRecoveryRef.current.disconnected = true;
    setConnectionStatus("failed");
  }
});

socket.on("seatExpired", () => {
  clearConnectionRecoveryTimeout();
  clearHeartbeatWarning();
  suppressConnectionStatusRef.current = true;
  connectionRecoveryRef.current.active = false;
  setConnectionStatus(null);
  setSeated(false);
  setIsReconnecting(false);
  setBootReady(true);
  localStorage.removeItem("rikiReconnectToken");
  localStorage.removeItem("rikiWasSeated");
  localStorage.removeItem(RECOVERY_KEY_STORAGE_KEY);
  setSeatExpired(true);
});

socket.on("gameTerminatedLastPlayer", () => {
  clearConnectionRecoveryTimeout();
  clearHeartbeatWarning();
  suppressConnectionStatusRef.current = true;
  setConnectionStatus(null);
  localStorage.removeItem("rikiReconnectToken");
  localStorage.removeItem("rikiWasSeated");
  localStorage.removeItem(RECOVERY_KEY_STORAGE_KEY);
  setSeated(false);
  setGameTerminationMessage("All other players disconnected, so the game has been terminated");
});

socket.on("gameOver", ({ finalScores, finalPlayers }) => {
      clearConnectionRecoveryTimeout();
      clearHeartbeatWarning();
      suppressConnectionStatusRef.current = true;
      gameStartedRef.current = false;
      connectionRecoveryRef.current = { active: false, disconnected: false, seatConfirmed: false, stateReceived: false };
      setIsReconnecting(false);
      // A completed game has no seat to recover after a reload or app reopen.
      localStorage.removeItem("rikiReconnectToken");
      localStorage.removeItem("rikiWasSeated");
      localStorage.removeItem(RECOVERY_KEY_STORAGE_KEY);
      setConnectionStatus(null);
      scoreboardStickToBottomRef.current = false;
      if (scoreboardScrollSaveTimerRef.current) {
        clearTimeout(scoreboardScrollSaveTimerRef.current);
        scoreboardScrollSaveTimerRef.current = null;
      }
      try {
        localStorage.removeItem(SCOREBOARD_SCROLL_STORAGE_KEY);
      } catch {
        // Ignore storage errors so the end screen still opens normally.
      }
      setFinalScoreboardData({
        players: sortScoreboardPlayers(finalPlayers, finalScores, socket.id),
        scores: finalScores
      });

      const results = finalPlayers.map(p => {
        const playerScores = finalScores[p.id] || [];
        const total = playerScores.reduce((sum, score) => sum + (score || 0), 0);
        return { ...p, totalScore: total };
      });

      results.sort((a, b) => b.totalScore - a.totalScore);

      let currentDisplayRank = 0;
      let lastScore = null;
      const rankedResults = results.map((player) => {
        if (player.totalScore !== lastScore) {
          currentDisplayRank++;
          lastScore = player.totalScore;
        }
        return { ...player, rank: currentDisplayRank };
      });

      setWinnerData(rankedResults);

      // Start the 3-second lockout timer
      setCanReload(false);
      setTimeout(() => {
        setCanReload(true);
      }, 3000);
    });

    socket.on("gameStateUpdate", (data) => {
      const newGameStarted = hasReceivedGameStateRef.current && !gameStartedRef.current &&
        data.gameStarted && data.currentRound === 1 && data.totalRoundsPlayed === 0;
      if (newGameStarted) {
        scoreboardStickToBottomRef.current = false;
        if (scoreboardScrollSaveTimerRef.current) {
          clearTimeout(scoreboardScrollSaveTimerRef.current);
          scoreboardScrollSaveTimerRef.current = null;
        }
        try {
          localStorage.removeItem(SCOREBOARD_SCROLL_STORAGE_KEY);
        } catch {
          // Keep the new game playable when storage is unavailable.
        }
        if (scoreboardScrollRef.current) scoreboardScrollRef.current.scrollTop = 0;
        if (IS_NATIVE_PHONE_APP) window.dispatchEvent(new Event("riki-scoreboard-new-game"));
      }
      hasReceivedGameStateRef.current = true;
      gameStartedRef.current = !!data.gameStarted;
      setGameState(data);
      if (data.scoreHistory) setScoreHistory(data.scoreHistory);
      if (connectionRecoveryRef.current.active) {
        connectionRecoveryRef.current.stateReceived = true;
        maybeCompleteConnectionRecovery();
      }
    });

    socket.on("playError", (msg) => alert(msg));
    socket.off("joinError").on("joinError", (msg) => {
      setSeated(false);
      alert(msg);
    });
    socket.off("startError").on("startError", (msg) => {
      if (window.RIKI_TIKI_LOAD_NATIVE_PHONE_APP) {
        setStartErrorMessage(msg);
      } else {
        alert(msg);
      }
    });
    
    return () => {
    clearConnectionRecoveryTimeout();
    clearHeartbeatWarning();
    window.removeEventListener("offline", onBrowserOffline);
    window.removeEventListener("online", onBrowserOnline);
    if (observedEngine) observedEngine.off("ping", onEnginePing);
    socket.off("disconnect", onSocketDisconnect);
    socket.off("connect_error", onConnectError);
    socket.off("connect");
    socket.off("playersUpdate");
    socket.off("seatConfirmed");
    socket.off("gameOver");
    socket.off("gameStateUpdate");
    socket.off("playError");
    socket.off("joinError");
    socket.off("startError");
    socket.off("reconnectFailed");
    socket.off("profileTakenOver");
    socket.off("guestSeatTakenOver");
    socket.off("profileLoginRejected");
    socket.off("profileAuthenticated");
    socket.off("profileLoginReady");
    socket.off("profilePasswordRequired");
    socket.off("profileAuthConfig");
    socket.off("seatExpired");
    socket.off("gameTerminatedLastPlayer");
};

    
  }, []);

  const reconnectToken = localStorage.getItem("rikiReconnectToken");

const me = players.find(
  p => p && (p.id === socket.id || p.reconnectToken === reconnectToken)
);
  const isMyTurn = gameState.bidsComplete && gameState.turnIndex !== -1 && players[gameState.turnIndex]?.id === socket.id;
  const isMyBidTurn = gameState.callerIndex !== -1 && players[gameState.callerIndex]?.id === socket.id;
  useEffect(() => {
    setBidConfirmationOpen(false);
  }, [apaMode, isMyBidTurn, gameState.gameStarted, gameState.bidsComplete, gameState.currentRound, selectedBid]);
  const activePlayers = players.filter(p => p !== null);
  const scoreboardPlayers = sortScoreboardPlayers(activePlayers, scoreHistory, me?.id || socket.id);
  useEffect(() => {
    if (!gameState.gameStarted || !me?.hand?.length) return;
    if (lastSortedHandRef.current.length !== me.hand.length ||
        !me.hand.every(card => lastSortedHandRef.current.some(saved => saved.id === card.id))) return;
    try {
      localStorage.setItem(HAND_ORDER_STORAGE_KEY, JSON.stringify(lastSortedHandRef.current.map(card => card.id)));
    } catch {
      // Keep the in-memory order when browser storage is unavailable.
    }
  }, [me, players, gameState, manualHandOrderIds]);
  const hasAnyBid = activePlayers.some(p => p.bid !== null);
  const bidDifference = gameState.totalBids - gameState.currentRound;
  const bidBalanceText = bidDifference === 0
    ? "-"
    : `${Math.abs(bidDifference)} ${bidDifference > 0 ? "FÖLÉ" : "ALÁ"}`;
  const bidBalanceClass = bidDifference === 0
    ? "equal"
    : bidDifference > 0 ? "over" : "under";

  useEffect(() => {
    draggingCardRef.current = draggingCard;
  }, [draggingCard]);

  useEffect(() => {
    reorderPreviewRef.current = reorderPreview;
  }, [reorderPreview]);

  useEffect(() => {
    isCardLegalRef.current = isCardLegal;
  }, [isCardLegal]);

  useEffect(() => {
    latestDragContextRef.current = {
      isMyTurn,
      me,
      playedCards: gameState.playedCards || []
    };
  }, [isMyTurn, me, gameState.playedCards]);

  const applySavedHandOrder = (hand, orderIds) => {
    if (!orderIds || !Array.isArray(orderIds)) return hand;

    const handIds = hand.map(card => card.id);
    const filteredOrder = orderIds.filter(id => handIds.includes(id));
    if (filteredOrder.length !== hand.length) return hand;

    const cardById = new Map(hand.map(card => [card.id, card]));
    return filteredOrder.map(id => cardById.get(id));
  };

  const commitHandOrder = (orderIds, fromIndex, insertIndex) => {
    if (!orderIds || fromIndex === insertIndex || fromIndex + 1 === insertIndex) return;

    const nextOrder = [...orderIds];
    const [movedId] = nextOrder.splice(fromIndex, 1);
    const targetIndex = insertIndex > fromIndex ? insertIndex - 1 : insertIndex;
    nextOrder.splice(targetIndex, 0, movedId);

    setManualHandOrderIds(nextOrder);
    try {
      localStorage.setItem(HAND_ORDER_STORAGE_KEY, JSON.stringify(nextOrder));
    } catch {}
  };

  const isPointerOverHandCard = useCallback((cardId, e) => {
    if (!cardId || !e || !handContainerRef.current) return false;
    const cardEl = handContainerRef.current.querySelector(`[data-hand-card-id="${cardId}"]`);
    if (!cardEl) return false;

    const rect = cardEl.getBoundingClientRect();
    return e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom;
  }, []);

  const releaseHandDrag = useCallback((hoverCardId = null, e = null) => {
    const releasedDragSession = dragSessionRef.current;

    if (!IS_NATIVE_PHONE_APP && hoverCardId && isPointerOverHandCard(hoverCardId, e)) {
      setPostDragHoverCardId(hoverCardId);
    } else {
      setPostDragHoverCardId(null);
    }

    draggingCardRef.current = null;
    reorderPreviewRef.current = null;

    setDraggingCard(null);
    setReorderPreview(null);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (dragSessionRef.current !== releasedDragSession || draggingCardRef.current) return;

        draggingCardRef.current = null;
        reorderPreviewRef.current = null;
        setDraggingCard(null);
        setReorderPreview(null);
        setSuppressHandTransition(false);

        if (IS_NATIVE_PHONE_APP) {
          setPostDragHoverCardId(null);
          setSelectedCardIndex(null);
        }
      });
    });
  }, [isPointerOverHandCard]);

  const downloadFinalScoreboard = () => {
    if (!finalScoreboardData) return;

    const exportPlayers = finalScoreboardData.players;
    const exportScores = finalScoreboardData.scores;
    const exportDate = new Date();
    const dateText = `${exportDate.getFullYear()}.${String(exportDate.getMonth() + 1).padStart(2, "0")}.${String(exportDate.getDate()).padStart(2, "0")}`;
    const timeText = `${String(exportDate.getHours()).padStart(2, "0")}.${String(exportDate.getMinutes()).padStart(2, "0")}`;

    const scale = 2;
    const width = Math.max(820, 120 + (exportPlayers.length * 110));
    const topPanelHeight = 58;
    const titleHeight = 66;
    const headerHeight = 44;
    const rowHeight = 38;
    const totalHeight = 46;
    const padding = 28;
    const tableRowsHeight = headerHeight + (ROUND_SEQUENCE.length * rowHeight) + totalHeight;
    const height = padding + topPanelHeight + titleHeight + tableRowsHeight + padding;

    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = height * scale;

    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);

    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, width, height);

    ctx.strokeStyle = "#444";
    ctx.lineWidth = 2;
    ctx.strokeRect(padding, padding, width - (padding * 2), height - (padding * 2));

    ctx.fillStyle = "#101010";
    ctx.fillRect(padding, padding, width - (padding * 2), topPanelHeight);
    ctx.fillStyle = "#ffffff";
    ctx.font = "900 24px Segoe UI, Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`Riki-Tiki Game - ${dateText} ${timeText}`, width / 2, padding + (topPanelHeight / 2));

    ctx.fillStyle = "#ff9800";
    ctx.font = "900 30px Segoe UI, Arial";
    ctx.letterSpacing = "6px";
    ctx.fillText("SCOREBOARD", width / 2, padding + topPanelHeight + (titleHeight / 2));
    ctx.letterSpacing = "0px";

    const tableX = padding;
    const tableY = padding + topPanelHeight + titleHeight;
    const tableWidth = width - (padding * 2);
    const roundColWidth = 70;
    const playerColWidth = (tableWidth - roundColWidth) / exportPlayers.length;

    const drawCell = (x, y, w, h, text, options = {}) => {
      ctx.fillStyle = options.background || "#0a0a0a";
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = options.border || "#222";
      ctx.lineWidth = options.lineWidth || 1;
      ctx.strokeRect(x, y, w, h);
      ctx.fillStyle = options.color || "#fff";
      ctx.font = options.font || "900 17px Segoe UI, Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, x + (w / 2), y + (h / 2), w - 10);
    };

    drawCell(tableX, tableY, roundColWidth, headerHeight, "RND", {
      background: "#151515",
      color: "#ff9800",
      border: "#555"
    });

    exportPlayers.forEach((p, idx) => {
      drawCell(tableX + roundColWidth + (idx * playerColWidth), tableY, playerColWidth, headerHeight, p.name, {
        background: "#0a0a0a",
        color: "#aaa",
        font: "900 15px Segoe UI, Arial",
        border: "#333"
      });
    });

    ROUND_SEQUENCE.forEach((roundValue, roundIdx) => {
      const y = tableY + headerHeight + (roundIdx * rowHeight);

      drawCell(tableX, y, roundColWidth, rowHeight, String(roundValue), {
        background: "#151515",
        color: "#ff9800",
        border: "#222"
      });

      exportPlayers.forEach((p, idx) => {
        const val = exportScores[p.id]?.[roundIdx];
        const isDefiniteValue = val !== undefined && val !== null;
        const scoreText = isDefiniteValue ? (val > 0 ? `+${val}` : String(val)) : "-";
        const color = val < 0 ? "#ff4444" : val > 0 ? "#00ff88" : "#fff";

        drawCell(tableX + roundColWidth + (idx * playerColWidth), y, playerColWidth, rowHeight, scoreText, {
          background: "#0a0a0a",
          color,
          border: "#222"
        });
      });

      if (roundIdx === 13) {
        ctx.strokeStyle = "#555";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(tableX, y);
        ctx.lineTo(tableX + tableWidth, y);
        ctx.stroke();
      }
    });

    const totalY = tableY + headerHeight + (ROUND_SEQUENCE.length * rowHeight);
    drawCell(tableX, totalY, roundColWidth, totalHeight, "TOT", {
      background: "#050505",
      color: "#00e5ff",
      border: "#555",
      font: "900 17px Segoe UI, Arial",
      lineWidth: 2
    });

    exportPlayers.forEach((p, idx) => {
      const total = (exportScores[p.id] || []).reduce((sum, score) => sum + (score || 0), 0);
      const color = total < 0 ? "#ff4444" : total > 0 ? "#00e5ff" : "#fff";
      drawCell(tableX + roundColWidth + (idx * playerColWidth), totalY, playerColWidth, totalHeight, String(total), {
        background: "#050505",
        color,
        border: "#555",
        font: "900 20px Segoe UI, Arial",
        lineWidth: 2
      });
    });

    ctx.strokeStyle = "#555";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(tableX, totalY);
    ctx.lineTo(tableX + tableWidth, totalY);
    ctx.stroke();

    const link = document.createElement("a");
    link.download = `riki-tiki-scoreboard-${dateText}-${timeText}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

const apaText = (() => {
  if (!apaMode || !gameState.gameStarted || !me) return "";

  // BIDDING PHASE
  if (!gameState.bidsComplete) {
    if (me.bid !== null) return "";
    if (isMyBidTurn) return "te mondasz";
    return "nem te mondasz";
  }

  // PLAYING PHASE
  if (gameState.bidsComplete) {
    if (isMyTurn) return "te jössz";
  }

  return "";
})();

  const handleCardPointerDown = (e, index, card, displayIndex, orderIds) => {
    if (!gameState.gameStarted || !me?.hand) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const phoneReorderSlot = IS_NATIVE_PHONE_APP && handContainerRef.current
      ? (() => {
          const cardLefts = Array.from(handContainerRef.current.querySelectorAll("[data-hand-card-id]"))
            .map(el => el.getBoundingClientRect().left)
            .sort((a, b) => a - b);
          const slotWidths = cardLefts
            .slice(1)
            .map((left, i) => left - cardLefts[i])
            .filter(width => width > 0.5)
            .sort((a, b) => a - b);

          return slotWidths.length
            ? slotWidths[Math.floor(slotWidths.length / 2)]
            : rect.width + 6;
        })()
      : 122;

    dragSessionRef.current += 1;
    draggingCardRef.current = null;
    reorderPreviewRef.current = null;
    setDraggingCard(null);
    setReorderPreview(null);
    setSuppressHandTransition(false);

    setSelectedCardIndex(null);
    setPostDragHoverCardId(null);
    const nextDraggingCard = {
      card,
      originalIndex: index,
      displayIndex,
      orderIds,
      x: e.clientX,
      y: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      reorderSlotWidth: phoneReorderSlot
    };

    draggingCardRef.current = nextDraggingCard;
    setDraggingCard(nextDraggingCard);
  };

  const isDraggingHandCard = !!draggingCard;

  useEffect(() => {
    if (!isDraggingHandCard) return;

    const handlePointerMove = (e) => {
      setDraggingCard(prev => {
        if (!prev) return null;

        if (handContainerRef.current) {
          const handRect = handContainerRef.current.getBoundingClientRect();
          const cardTop = e.clientY - prev.offsetY;
          const cardBottom = cardTop + prev.height;
          const cardEls = Array.from(handContainerRef.current.querySelectorAll("[data-hand-card-id]"));
          const otherCardEls = cardEls.filter(el => el.dataset.handCardId !== prev.card.id);
          const rowRects = otherCardEls.map(el => el.getBoundingClientRect());
          const rowTop = rowRects.length ? Math.min(...rowRects.map(rect => rect.top)) : handRect.top;
          const rowBottom = rowRects.length ? Math.max(...rowRects.map(rect => rect.bottom)) : handRect.bottom;
          const rowOverlap = Math.min(cardBottom, rowBottom) - Math.max(cardTop, rowTop);
          const minOverlap = prev.height * 0.1;
          const isReorderZone = rowOverlap >= minOverlap;

          if (isReorderZone) {
            const cardLeft = (e.clientX - handRect.left) - prev.offsetX;
            const draggedCenter = cardLeft + (prev.width / 2);
            const allStableSlots = cardEls
              .map(el => {
                const rect = el.getBoundingClientRect();
                return {
                  id: el.dataset.handCardId,
                  left: rect.left - handRect.left,
                  width: rect.width
                };
              })
              .sort((a, b) => a.left - b.left);
            const orderedOtherSlots = allStableSlots
              .filter(slot => slot.id !== prev.card.id)
              .map(slot => ({
                ...slot,
                displayIndex: prev.orderIds.indexOf(slot.id),
                center: slot.left + (slot.width / 2)
              }))
              .sort((a, b) => a.displayIndex - b.displayIndex);
            const targetIndexWithoutDragged = orderedOtherSlots
              .filter(slot => draggedCenter > slot.center)
              .length;
            const insertIndex = targetIndexWithoutDragged <= prev.displayIndex
              ? targetIndexWithoutDragged
              : targetIndexWithoutDragged + 1;
            const rawStrength = Math.max(0, Math.min(1, (rowOverlap - minOverlap) / (prev.height * 0.47)));
            const strength = Number((rawStrength * rawStrength).toFixed(3));

            if (insertIndex === prev.displayIndex || insertIndex === prev.displayIndex + 1) {
              setReorderPreview(null);
            } else {
              setReorderPreview(current => (
                current?.active &&
                current.insertIndex === insertIndex &&
                current.draggedIndex === prev.displayIndex &&
                Math.abs((current.strength || 0) - strength) < 0.02 &&
                !(strength === 1 && current.strength !== 1)
              ) ? current : {
                active: true,
                insertIndex,
                draggedIndex: prev.displayIndex,
                strength
              });
            }
          } else {
            setReorderPreview(null);
          }
        }

        return { ...prev, x: e.clientX, y: e.clientY };
      });
    };

    const handlePointerUp = (e) => {
      const currentDrag = draggingCardRef.current;
      const currentReorderPreview = reorderPreviewRef.current;
      const {
        isMyTurn: latestIsMyTurn,
        me: latestMe,
        playedCards: latestPlayedCards
      } = latestDragContextRef.current;

      if (currentDrag && currentReorderPreview?.active) {
        setSuppressHandTransition(true);
        commitHandOrder(currentDrag.orderIds, currentDrag.displayIndex, currentReorderPreview.insertIndex);
        releaseHandDrag(currentDrag.card.id, e);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setSuppressHandTransition(false));
        });
        return;
      }

      if (currentDrag && playAreaRef.current) {
        const rect = playAreaRef.current.getBoundingClientRect();
        const isOver = e.clientX > rect.left && e.clientX < rect.right && e.clientY > rect.top && e.clientY < rect.bottom;
        const alreadyPlayedThisTrick = latestMe?.playerId &&
          latestPlayedCards.some(pc => pc?.playerId === latestMe.playerId);

        if (isOver && latestIsMyTurn && latestMe && !alreadyPlayedThisTrick && isCardLegalRef.current?.(currentDrag.card, latestMe.hand)) {
          const playedIndex = latestPlayedCards.length || 0;
          const isNativePhone = !!document.querySelector(".native-phone-app");
          const targetLeft = isNativePhone ? 13 + (playedIndex * 42) : 20 + (playedIndex * 64);
          const targetTop = isNativePhone ? 5 : 8;
          const targetWidth = isNativePhone ? currentDrag.width : 110;
          const targetHeight = isNativePhone ? currentDrag.height : 160;
          const from = {
            left: Math.round(e.clientX - currentDrag.offsetX),
            top: Math.round(e.clientY - currentDrag.offsetY),
            width: currentDrag.width,
            height: currentDrag.height
          };
          const to = {
            left: Math.round(rect.left + targetLeft),
            top: Math.round(rect.top + targetTop),
            width: targetWidth,
            height: targetHeight
          };

          setPlayFlight({
            card: currentDrag.card,
            startTransform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width}, ${from.height / to.height})`,
            landing: false
          });

          requestAnimationFrame(() => {
            setPlayFlight(prev => prev && prev.card.id === currentDrag.card.id
              ? { ...prev, landing: true }
              : prev
            );
          });

          socket.emit("playCard", currentDrag.originalIndex);
        }
      }

      releaseHandDrag(currentDrag?.card.id, e);
    };

    const handlePointerCancel = () => {
      releaseHandDrag();
    };

    const handleWindowBlur = () => {
      releaseHandDrag();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        releaseHandDrag();
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);
    window.addEventListener("blur", handleWindowBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
      window.removeEventListener("blur", handleWindowBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isDraggingHandCard, releaseHandDrag]);

  useEffect(() => {
    if (!draggingCard || !me?.hand) return;

    const draggedCardStillInHand = me.hand.some(card => card.id === draggingCard.card.id);
    if (!draggedCardStillInHand) {
      releaseHandDrag();
    }
  }, [draggingCard, me?.hand, releaseHandDrag]);

  const playFlightCardId = playFlight?.card?.id;

  useEffect(() => {
    if (!playFlightCardId) return;

    const settleTimer = setTimeout(() => {
      setPlayFlight(prev => prev && prev.card.id === playFlightCardId ? { ...prev, exiting: true } : prev);
    }, 155);

    const clearTimer = setTimeout(() => {
      setPlayFlight(prev => prev && prev.card.id === playFlightCardId ? null : prev);
    }, 225);

    return () => {
      clearTimeout(settleTimer);
      clearTimeout(clearTimer);
    };
  }, [playFlightCardId]);

  const isLastBidder = () => {
    const active = players.filter(p => p !== null);
    const bidsDone = active.filter(p => p.bid !== null).length;
    return bidsDone === active.length - 1;
  };

  const submitSelectedBid = () => {
    if (!gameState.gameStarted || gameState.bidsComplete || !isMyBidTurn ||
        selectedBid === null || selectedBid > gameState.currentRound ||
        (isLastBidder() && gameState.totalBids + selectedBid === gameState.currentRound)) return;
    socket.emit("submitBid", selectedBid);
    setSelectedBid(null);
    setBidConfirmationOpen(false);
  };

  

  const smartWrap = (name) => {
    if (!colWidth) return { line1: name, line2: "" };
    const limit = colWidth - 10; 
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = "bold 14px Arial"; 

    let line1 = "";
    let line2 = "";
    let i = 0;

    for (; i < name.length; i++) {
      const testLine = line1 + name[i];
      if (context.measureText(testLine).width > limit) break;
      line1 = testLine;
    }

    for (; i < name.length; i++) {
      const testLine = line2 + name[i];
      if (context.measureText(testLine + "...").width > limit) {
        if (i < name.length - 1) line2 += "...";
        else if (context.measureText(testLine).width <= limit) line2 = testLine;
        else line2 += "...";
        break;
      }
      line2 = testLine;
    }
    return { line1, line2 };
  };

  return (
    <div className={IS_NATIVE_PHONE_APP
      ? `App phone-app native-phone-app${draggingCard?.orderIds.length <= 11 ? " phone-separated-drag-shadow" : ""}`
      : "App"} data-player-count={IS_NATIVE_PHONE_APP ? activePlayers.length : undefined}>
      <div className="scoreboard">
        <h3>Scoreboard</h3>
        <div
          className="score-table-wrapper"
          ref={scoreboardScrollRef}
          onScroll={(event) => {
            if (IS_NATIVE_PHONE_APP || !scoreboardScrollRestoredRef.current) return;

            const wrapper = event.currentTarget;
            const scrollTop = wrapper.scrollTop;
            const distanceFromBottom = wrapper.scrollHeight - wrapper.clientHeight - scrollTop;
            const isAtBottom = distanceFromBottom <= 3;
            scoreboardStickToBottomRef.current = isAtBottom;
            const positionToSave = isAtBottom ? "bottom" : String(scrollTop);
            if (scoreboardScrollSaveTimerRef.current) {
              clearTimeout(scoreboardScrollSaveTimerRef.current);
            }

            scoreboardScrollSaveTimerRef.current = setTimeout(() => {
              try {
                localStorage.setItem(SCOREBOARD_SCROLL_STORAGE_KEY, positionToSave);
              } catch {
                // Ignore storage errors so scrolling continues normally.
              }
              scoreboardScrollSaveTimerRef.current = null;
            }, 1);
          }}
        >
          <table className="score-table">
            <thead>
              <tr>
                <th className="round-label-cell">RND</th>
                {scoreboardPlayers.map((p, idx) => {
                  const { line1, line2 } = smartWrap(p.name);
                  return (
                    <th 
                      key={p.id} 
                      ref={idx === 0 ? columnRef : null} 
                      className={p.id === socket.id ? "my-column" : ""} 
                      title={p.name}
                    >
                      {line1}
                      {line2 && <><br />{line2}</>}
                    </th>
                  );
                })}
                <th className="scroller-gutter"></th>
              </tr>
            </thead>
            <tbody>
              {ROUND_SEQUENCE.map((rVal, rIdx) => (
                <tr key={rIdx} className={rIdx === 13 ? "halfway-divider" : ""}>
                  <td className="round-label-cell">{rVal}</td>
                  {scoreboardPlayers.map((p) => {
                    const val = scoreHistory[p.id]?.[rIdx];
                    const isDefiniteValue = val !== undefined && val !== null;
                    const cellClasses = [
                      p.id === socket.id ? "my-column" : "",
                      val < 0 ? "neg-score" : val > 0 ? "pos-score" : ""
                    ].join(" ");
                    return (
                      <td key={p.id} className={cellClasses}>
                        {isDefiniteValue ? (val > 0 ? `+${val}` : val) : "-"}
                      </td>
                    );
                  })}
                  <td className="scroller-gutter"></td>
                </tr>
              ))}
              <tr className="total-row">
                <td className="round-label-cell">TOT</td>
                {scoreboardPlayers.map((p) => {
                  const playerScores = scoreHistory[p.id] || [];
                  const total = playerScores.reduce((acc, curr) => acc + (curr || 0), 0);
                  const cellClasses = [
                    p.id === socket.id ? "my-column" : "",
                    total < 0 ? "neg-score" : total > 0 ? "pos-score" : ""
                  ].join(" ");
                  return (
                    <td key={p.id} className={cellClasses}>{total}</td>
                  );
                })}
                <td className="scroller-gutter"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {!winnerData && (
        <button
          className={`profile-status ${gameState.gameStarted && !activeProfile ? "disabled" : ""}`}
          disabled={gameState.gameStarted && !activeProfile}
          onClick={() => {
            if (profileModalOpen) cancelPendingProfileLogin();
            setProfileModalOpen(prev => {
              if (prev) setShowProfileChoices(false);
              return !prev;
            });
          }}
        >
          {activeProfile ? (
            <>
              Logged in as <span>{activeProfile.label}</span>
            </>
          ) : "Login"}
        </button>
      )}

      {!winnerData && !gameState.gameStarted && seated && gameState.totalRoundsPlayed === 0 && (
        <button className="phone-start-game-btn" onClick={() => socket.emit("startGame")}>START GAME</button>
      )}

      {takeoverMessage && (
        <div className="profile-taken-over-overlay">
          <div className="profile-taken-over-card">
            {takeoverMessage.kind === "guest" ? (
              <h2>Your guest seat was opened in another tab or device.</h2>
            ) : (
              <h2><span>{takeoverMessage.profileLabel}</span> logged in from a new device.</h2>
            )}
            <p>This device is no longer controlling your seat.</p>
          </div>
          {IS_NATIVE_PHONE_APP && (
            <button type="button" className="phone-refresh-btn" aria-label="Refresh page" onClick={() => window.location.reload()}>
              <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19.9 14a8 8 0 1 1-3.4-8.4" />
                <path d="M20 4v5h-5" />
              </svg>
            </button>
          )}
        </div>
      )}

      {startErrorMessage && (
        <div className="popup start-error-popup">
          <h2>{startErrorMessage}</h2>
          <button onClick={() => setStartErrorMessage("")}>OK</button>
        </div>
      )}

      {profileModalOpen && (!gameState.gameStarted || activeProfile) && (
        <div className={`popup profile-popup ${activeProfile && !showProfileChoices ? "profile-logged-in-popup" : "profile-chooser-popup"}`}>
          {passwordProfileId ? (
            <form className="profile-password-form" onSubmit={(event) => {
              event.preventDefault();
              profilePasswordInputRef.current?.focus();
              if (!profilePassword) {
                return;
              }
              requestProfileLogin(passwordProfileId, profilePassword);
            }}>
              <div className="profile-password-heading">
                <img src={FAMILY_PROFILES.find(profile => profile.id === passwordProfileId)?.avatar} alt="" draggable={false} />
                <h2>{FAMILY_PROFILES.find(profile => profile.id === passwordProfileId)?.label}</h2>
              </div>
              <input ref={profilePasswordInputRef} id="profile-password" type="password" aria-label="Password"
                placeholder={profilePasswordError || "Enter password"}
                className={profilePasswordError ? "profile-password-input-error" : ""}
                aria-invalid={!!profilePasswordError}
                aria-describedby={profilePasswordError ? "profile-password-error" : undefined}
                autoComplete="current-password" autoFocus
                value={profilePassword} onChange={(event) => {
                  setProfilePassword(event.target.value);
                  setProfilePasswordError("");
                }} />
              {profilePasswordError && <p id="profile-password-error" className="profile-password-error" role="alert">{profilePasswordError}</p>}
              <div className="profile-password-actions">
                <button type="submit" onPointerDown={(event) => {
                  event.preventDefault();
                  profilePasswordInputRef.current?.focus();
                }}>Log in</button>
                <button type="button" className="guest-btn" onClick={() => {
                  cancelPendingProfileLogin();
                  setPasswordProfileId(null);
                  setProfilePassword("");
                  setProfilePasswordError("");
                }}>Back</button>
              </div>
            </form>
          ) : activeProfile && !showProfileChoices ? (
            <>
              <h2>{activeProfile.label}</h2>
              <img className="profile-modal-avatar" src={activeProfile.avatar} alt={activeProfile.label} draggable={false} />
              <button className="logout-btn" disabled={gameState.gameStarted} onClick={logoutProfile}>Log out</button>
              <button className="guest-btn" onClick={() => {
                cancelPendingProfileLogin();
                setProfileModalOpen(false);
              }}>Close</button>
            </>
          ) : (
            <>
              <h2>Choose profile</h2>
              <div className="profile-choice-list">
                {FAMILY_PROFILES.map(profile => (
                  <button
                    key={profile.id}
                    className="profile-choice-btn"
                    onClick={() => chooseProfile(profile.id)}
                  >
                    <img src={profile.avatar} alt={profile.label} draggable={false} />
                    <span>{profile.label}</span>
                  </button>
                ))}
              </div>
              <button className="guest-btn" onClick={() => {
                cancelPendingProfileLogin();
                setProfileModalOpen(false);
                setShowProfileChoices(false);
              }}>Close</button>
            </>
          )}
        </div>
      )}

      {bootReady && !seated && !isReconnecting && !gameState.gameStarted && (!IS_NATIVE_PHONE_APP || !gameTerminationMessage) && (
        <div className="popup seat-popup">
          <h2>Take a seat</h2>
          <input 
  type="text" 
  autoFocus={!IS_NATIVE_PHONE_APP}
  value={yourName} 
  onChange={(e) => setYourName(e.target.value)} 
  placeholder="Enter name (max 15)" 
  maxLength={15}
  onKeyDown={(e) => {
    if (e.key === "Enter" && yourName) {
      submitSeatRequest();
    }
  }}
/>
          <button onClick={submitSeatRequest}>Sit down</button>
        </div>
      )}



      {gameState.gameStarted && !gameState.bidsComplete && (
        <div className={`bid-sidebar ${!isMyBidTurn ? "view-only" : ""}`}>
          <div className="bid-scroll-list">
            {[...Array(14)].map((_, i) => {
              const forbidden = isLastBidder() && (gameState.totalBids + i === gameState.currentRound);
              const isTooHigh = i > gameState.currentRound;
              return (
                <button 
                  key={i} disabled={isTooHigh || forbidden || !isMyBidTurn}
                  className={`bid-num ${selectedBid === i ? "selected" : ""} ${ (isTooHigh || forbidden) ? "disabled" : ""}`}
                  onClick={() => setSelectedBid(currentBid => currentBid === i ? null : i)}
                >
                  {i}
                </button>
              );
            })}
          </div>
          <button
            className="ok-btn"
            disabled={!isMyBidTurn || selectedBid === null}
            onClick={() => {
              if (isMyBidTurn && selectedBid !== null) {
                if (apaMode) setBidConfirmationOpen(true);
                else submitSelectedBid();
              }
            }}
          >
            OK
          </button>
        </div>
      )}

      {bidConfirmationOpen && (
        <div className="bid-confirmation-overlay">
          <div className="popup bid-confirmation-popup" role="dialog" aria-modal="true" aria-labelledby="bid-confirmation-title">
            <h2 id="bid-confirmation-title">Biztos hogy ennyit mondasz?</h2>
            <div className="bid-confirmation-number">{selectedBid}</div>
            <div className="bid-confirmation-actions">
              <button type="button" onClick={submitSelectedBid}>Igen</button>
              <button type="button" className="bid-confirmation-no" onClick={() => setBidConfirmationOpen(false)}>Nem</button>
            </div>
          </div>
        </div>
      )}

      <div className="room-background">
        <div className="poker-table">
          <div className="round-header">
            <div className="round-counter">ROUND {gameState.currentRound}</div>
          </div>
          {gameState.gameStarted && hasAnyBid && (
            <div className={`bid-balance-counter ${bidBalanceClass}`}>{bidBalanceText}</div>
          )}
          {connectionStalled && !connectionStatus && !winnerData && !takeoverMessage && !gameTerminationMessage && (
            <div className="connection-stall-warning" role="status" aria-label="Game connection may be stalled" title="Game connection may be stalled">
              <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
                <path d="M4 13a23 23 0 0 1 26 0M9 19a15 15 0 0 1 16 0M14 25a7 7 0 0 1 6 0" />
                <circle className="connection-stall-dot" cx="17" cy="30" r="2" />
                <circle className="connection-stall-badge" cx="30" cy="29" r="8" />
                <path className="connection-stall-exclamation" d="M30 25v5M30 33v.5" />
              </svg>
            </div>
          )}
          {apaMode && apaText && (
  <div
    className={`apa-helper-text ${
      !gameState.bidsComplete
        ? (isMyBidTurn ? "bid-your-turn" : "bid-wait")
        : "play-turn"
    }`}
  >
    {apaText}
  </div>
)}

          <div className="center-container">
            {apaMode && gameState.playedCards?.length > 0 && (
              <div className="apa-played-card-labels">
                {gameState.playedCards.map((pc, idx) => {
                  if (!pc?.card) return null;
                  const player = players.find(p => p?.playerId === pc.playerId);
                  if (!player) return null;
                  const profile = FAMILY_PROFILES.find(p => p.id === player.profileId);
                  const fullName = profile ? profile.label : player.name;
                  const fittedName = fitApaPlayedCardName(fullName);
                  return (
                    <span
                      key={pc.card.id}
                      className={`apa-played-card-name ${profile ? `profile-${profile.id}` : "guest"}`}
                      aria-label={fullName}
                      style={{
                        left: `${21 + idx * 64 + 36}px`,
                        "--phone-left": `${13 + idx * 42 + 23}px`
                      }}
                    >
                      {fittedName.text}
                      {fittedName.truncated && <span className="apa-name-ellipsis">…</span>}
                    </span>
                  );
                })}
              </div>
            )}
            {gameState.gameStarted && gameState.trumpCard && (
              <div className={`trump-zone ${gameState.currentRound === 1 ? "round-one-trump" : ""}`}>
                <div className="card trump-face-up">
                  <img
                    src={getSvgPath(gameState.trumpCard.image)}
                    alt="trump"
                    className="card-img"
                  />
                </div>
                <span className="trump-label">TRUMP</span>
              </div>
            )}

            <div
  className="played-cards-area"
  ref={playAreaRef}
  onClick={() => {
    if (
      selectedCardIndex !== null &&
      isMyTurn &&
      me &&
      isCardLegal(me.hand[selectedCardIndex], me.hand)
    ) {
      socket.emit("playCard", selectedCardIndex);
      setSelectedCardIndex(null);
    }
  }}
>
    {(() => {
      const tableCards = [...(gameState.playedCards || [])];
      if (playFlight && !tableCards.some(pc => pc?.card?.id === playFlight.card.id)) {
        tableCards.push({ card: playFlight.card });
      }

      return tableCards.map((pc, idx) => (
        pc && pc.card && (
          <div
            key={pc.card.id}
            className={`card played-card ${playFlight?.card.id === pc.card.id ? "played-card-flight" : ""}`}
            style={{
              left: `${20 + (idx * 64)}px`,
              zIndex: idx,
              transform: playFlight?.card.id === pc.card.id
                ? (playFlight.landing ? "translate(0, 0) scale(1, 1)" : playFlight.startTransform)
                : undefined,
              transformOrigin: playFlight?.card.id === pc.card.id ? "top left" : undefined
            }}
          >
            <img
              src={pc.card.image ? getSvgPath(pc.card.image) : getBackPath(pc.card.deckColor)}
              alt="played"
              className="card-img"
            />
          </div>
        )
      ));
    })()}
    
    {/* This shows the 'Start Game' button only at the very beginning */}
    {!gameState.gameStarted && seated && gameState.totalRoundsPlayed === 0 && (
       <button className="start-game-btn" onClick={() => socket.emit("startGame")}>START GAME</button>
    )}

    {/* Optional: Show a message during the 5-second automatic pause */}
    {!gameState.gameStarted && gameState.totalRoundsPlayed > 0 && (
      <div className="next-round-status">Dealing next round soon...</div>
    )}
</div>
          </div>
          
          {players.map((p, i) => p && ( /* here */
            <div 
  key={i} 
  className={`seat seat-${getVisualSeat(i)} ${p.disconnected ? "seat-disconnected" : ""} ${p.disconnected && p.reconnectTimeLeft === 0 ? "permanent-dc" : ""}`}
>
              {gameState.gameStarted && gameState.currentRound === 1 && p.hand?.length > 0 && !(p.id === socket.id || p.reconnectToken === reconnectToken) && (
                <div className="card indian-card">
                  <img
                    src={hideCards ? HIDDEN_CARD_PATH : (p.hand[0].image ? getSvgPath(p.hand[0].image) : getBackPath(p.hand[0].deckColor))}
                    alt="indian"
                    className="card-img"
                  />
                </div>
              )}
              
              {gameState.gameStarted && p.tricksWon > 0 && ( /* p.tricksWon > 0 && */
                // ... find this section inside your return()
<div 
  className={`trick-indicator trick-pos-${getVisualSeat(i)} ${p.id === socket.id ? "my-trick-indicator" : ""}`}
  style={{ 
    backgroundImage: "url('/cards/back/TricksWon.svg')", 
    cursor: p.id === socket.id ? "pointer" : "default" 
  }}
  onClick={() => {
    if (p.id === socket.id) {
      setIsTricksPeeking(false);
      setIsViewingTricks(true);
    }
  }}
>
  <span>{p.tricksWon || 0}</span>
</div>
              )}
              {/* Opponent Mini-Hand (Backs Only) */}
{/* Opponent Mini-Hand: Now using the Simplified assets */}
{/* Opponent Mini-Hand: Now using the Simplified assets */}
{/* Opponent Mini-Hand: Triple-Layer Fan Logic */}
{/* Opponent Mini-Hand: Two-Layer Fan Logic */}
{gameState.gameStarted && gameState.currentRound > 1 && p.id !== socket.id && p.hand?.length > 0 && (
  <div style={{ position: 'relative' }}>
    
    {/* ROW 1: Bottom Layer (Cards 1-6) */}
    <div className="opponent-hand-grid">
      {p.hand.slice(0, 6).map((card, cardIdx) => (
        <div 
          key={cardIdx} 
          className="mini-card-back-wrapper"
          style={{ 
            "--i": cardIdx, 
            "--total": Math.min(p.hand.length, 6) 
          }}
        >
          <img 
            src={getSimpleBackPath(card.deckColor)} 
            alt="back" 
            className="mini-card-back-img" 
          />
        </div>
      ))}
    </div>

    {/* ROW 2: Top Layer (Cards 7+) */}
    {p.hand.length > 6 && (
      <div className="opponent-hand-grid-2">
        {p.hand.slice(6).map((card, cardIdx) => (
          <div 
            key={cardIdx} 
            className="mini-card-back-wrapper"
            style={{ 
              "--i": cardIdx, 
              "--total": p.hand.slice(6).length 
            }}
          >
            <img 
              src={getSimpleBackPath(card.deckColor)} 
              alt="back" 
              className="mini-card-back-img" 
            />
          </div>
        ))}
      </div>
    )}
  </div>
)}
              <div className="avatar-wrapper">
  <img
    src={p.avatar}
    className={`avatar ${gameState.turnIndex === i ? "active-turn" : ""} ${gameState.callerIndex === i ? "active-caller" : ""}`}
    alt={p.name}
  />

  {p.disconnected && (
    <div className="dc-overlay">
      <div className="dc-text">DISCONNECTED</div>
      {p.reconnectTimeLeft > 0 && (
        <div className="dc-timer">
  {(dcTimers[p.id || p.reconnectToken] ?? p.reconnectTimeLeft)}s
</div>
      )}
    </div>
  )}
</div>
              <div className="player-name-container">
  <div className={`player-name ${gameState.turnIndex === i ? "active-turn-name" : ""} ${gameState.callerIndex === i ? "active-caller-name" : ""}`}>{p.name}</div>
  {p.bid !== null && <div className="bid-badge">{p.bid}</div>}
</div>
            </div>
          ))}
        </div>
      </div>

      <div ref={handContainerRef} className={`hand-container ${draggingCard ? "is-dragging-card" : ""} ${suppressHandTransition ? "suppress-reorder-transition" : ""}`}>
  {gameState.gameStarted && me?.hand && (() => {
    // 1. Map original indices so we don't lose track of server-side position
    const handWithIndices = me.hand.map((card, originalIndex) => ({ 
      ...card, 
      originalIndex 
    }));

    let finalDisplayHand = [];

    const buildSortedDisplayHand = () => {
      const trumpSuit = gameState.trumpCard?.suit;
      const counts = me.hand.reduce((acc, card) => {
        acc[card.suit] = (acc[card.suit] || 0) + 1;
        return acc;
      }, {});

      let suitPriority = {};
      if (gameState.currentRound === 13 && !trumpSuit) {
        const isRed = (s) => s === 'H' || s === 'D';
        const presentSuits = ['S', 'H', 'C', 'D'].filter(suit => counts[suit]);
        let suitOrder;

        if (presentSuits.length === 3) {
          // Keep the only suit of its colour between the two suits of the other colour.
          const pairedSuits = presentSuits.filter(suit =>
            presentSuits.some(other => other !== suit && isRed(other) === isRed(suit))
          ).sort((a, b) => counts[b] - counts[a]);
          const separator = presentSuits.find(suit => isRed(suit) !== isRed(pairedSuits[0]));
          suitOrder = [pairedSuits[0], separator, pairedSuits[1]];
        } else {
          const lastSuit = [...presentSuits].sort((a, b) => counts[a] - counts[b])[0];
          const opposites = (isRed(lastSuit) ? ['S', 'C'] : ['H', 'D'])
            .sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
          const otherSameColor = isRed(lastSuit)
            ? (lastSuit === 'D' ? 'H' : 'D')
            : (lastSuit === 'S' ? 'C' : 'S');
          // Build the usual colour-alternating order from right to left.
          suitOrder = [opposites[1], otherSameColor, opposites[0], lastSuit];
        }

        suitPriority = Object.fromEntries(suitOrder.map((suit, index) => [suit, index]));
      } else if (!trumpSuit) {
        const sortedByCount = ['S', 'H', 'C', 'D'].sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
        const primarySuit = sortedByCount[0];
        const isRed = (s) => s === 'H' || s === 'D';
        const primaryIsRed = isRed(primarySuit);
        const opposites = primaryIsRed ? ['S', 'C'] : ['H', 'D'];
        opposites.sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
        const otherSameColor = primaryIsRed ? (primarySuit === 'D' ? 'H' : 'D') : (primarySuit === 'S' ? 'C' : 'S');
        suitPriority = { [primarySuit]: 0, [opposites[0]]: 1, [otherSameColor]: 2, [opposites[1]]: 3 };
      } else {
        const isRed = (s) => s === 'H' || s === 'D';
        const trumpIsRed = isRed(trumpSuit);
        const opposites = trumpIsRed ? ['S', 'C'] : ['H', 'D'];
        opposites.sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
        const otherSameColor = trumpIsRed ? (trumpSuit === 'D' ? 'H' : 'D') : (trumpSuit === 'S' ? 'C' : 'S');
        suitPriority = { [trumpSuit]: 0, [opposites[0]]: 1, [otherSameColor]: 2, [opposites[1]]: 3 };
      }

      return [...handWithIndices].sort((a, b) => {
        if (a.suit !== b.suit) return suitPriority[a.suit] - suitPriority[b.suit];
        return VALUE_ORDER_MAP[b.value] - VALUE_ORDER_MAP[a.value];
      });
    };

    // Reuse the saved order, including after refresh/reconnect, removing played cards.
    const cardsById = new Map(handWithIndices.map(card => [card.id, card]));
    const cached = lastSortedHandRef.current
      .filter(card => cardsById.has(card.id))
      .map(card => cardsById.get(card.id));

    let rememberOrder = true;
    if (cached.length === me.hand.length) {
      finalDisplayHand = cached;
    } else if (!gameState.bidsComplete && me.hand.length === gameState.currentRound) {
      // Only a newly dealt full hand may trigger the automatic sort.
      finalDisplayHand = buildSortedDisplayHand();
    } else {
      // With no saved order mid-round, retain the received order instead of sorting again.
      finalDisplayHand = handWithIndices;
      rememberOrder = false;
    }

    finalDisplayHand = applySavedHandOrder(finalDisplayHand, manualHandOrderIds);
    if (rememberOrder) lastSortedHandRef.current = finalDisplayHand;

    // 3. Render
    const finalDisplayOrderIds = finalDisplayHand.map(card => card.id);
    const getReorderTransform = (displayIndex, cardId) => {
      if (!reorderPreview?.active || !draggingCard || draggingCard.card.id === cardId) return undefined;

      const from = reorderPreview.draggedIndex;
      const to = reorderPreview.insertIndex;
      const slotWidth = draggingCard.reorderSlotWidth * (reorderPreview.strength ?? 1);

      if (to > from && displayIndex > from && displayIndex < to) {
        return `translateX(-${slotWidth}px)`;
      }

      if (to < from && displayIndex >= to && displayIndex < from) {
        return `translateX(${slotWidth}px)`;
      }

      return undefined;
    };

    return finalDisplayHand.map((card, displayIndex) => {
      const legal = isCardLegal(card, me.hand);
      return (
        <div
  key={card.id}
  data-hand-card-id={card.id}
  className={`card-wrapper 
    ${isPeeking ? "peeking" : ""} 
    ${(!legal && isMyTurn) ? "disabled" : ""} 
    ${selectedCardIndex === card.originalIndex ? "selected-card" : ""}
    ${postDragHoverCardId === card.id ? "post-drag-hover" : ""}
    ${(draggingCard?.card.id === card.id || playFlight?.card.id === card.id) ? "dragging-source" : ""}
  `}
  style={{
    transform: getReorderTransform(displayIndex, card.id),
    zIndex: IS_NATIVE_PHONE_APP && !draggingCard ? displayIndex + 1 : undefined
  }}
  draggable={false}
  onPointerDown={(e) => handleCardPointerDown(e, card.originalIndex, card, displayIndex, finalDisplayOrderIds)}
  onPointerLeave={() => {
    if (postDragHoverCardId === card.id) {
      setPostDragHoverCardId(null);
    }
  }}
  onClick={() => {
    if (!isMyTurn || !legal) return;

    setSelectedCardIndex(
      selectedCardIndex === card.originalIndex ? null : card.originalIndex
    );
  }}
>
          <div className="card-inner">
            <div className="card-face card-front">
              <img 
                src={hideCards && gameState.currentRound !== 1 ? HIDDEN_CARD_PATH : (gameState.currentRound === 1 ? getBackPath(card.deckColor) : getSvgPath(card.image))}
                alt="front" 
                className="card-img" 
              />
            </div>
            <div className="card-face card-back">
              <img src={getBackPath(card.deckColor)} alt="back" className="card-img" />
            </div>
          </div>
        </div>
      );
    });
  })()}

  {gameState.gameStarted && (
  <label className="apa-toggle">
  <input 
    type="checkbox" 
    checked={apaMode} 
    onChange={(e) => setApaMode(e.target.checked)} 
  />
  <span>APA SEGÉD</span>
</label>
)}

  {gameState.gameStarted && (
  <label className="apa-toggle hide-cards-toggle">
  <input
    type="checkbox"
    checked={hideCards}
    onChange={(e) => setHideCards(e.target.checked)}
  />
  <span>HIDE CARDS</span>
</label>
)}

  {gameState.gameStarted && gameState.currentRound > 1 && me?.hand?.length > 0 && (
    <button 
      className={`peek-btn ${isPeeking ? "active" : ""}`} 
      onTouchStart={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsPeeking(true);
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsPeeking(false);
      }}
      onTouchCancel={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsPeeking(false);
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        setIsPeeking(true);
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        setIsPeeking(false);
      }}
      onPointerCancel={(e) => {
        e.stopPropagation();
        setIsPeeking(false);
      }}
      onMouseDown={() => setIsPeeking(true)} 
      onMouseUp={() => setIsPeeking(false)}
      onMouseLeave={() => setIsPeeking(false)}
    >
      FLIP CARDS
    </button>
  )}
</div>

      {isViewingTricks && (
        <div className="tricks-overlay" onClick={() => {
          setIsTricksPeeking(false);
          setIsViewingTricks(false);
        }}>
          <div className="tricks-layout" onClick={(e) => e.stopPropagation()}>
          <div className="tricks-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tricks-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <h2>My Won Tricks</h2>
                {/* Adding the flip button to the modal header */}
                <button 
                  className={`peek-btn ${isTricksPeeking ? "active" : ""}`}
                  style={{ position: 'static' }} // Override absolute positioning from CSS
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsTricksPeeking(true);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsTricksPeeking(false);
                  }}
                  onTouchCancel={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsTricksPeeking(false);
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setIsTricksPeeking(true);
                  }}
                  onPointerUp={(e) => {
                    e.stopPropagation();
                    setIsTricksPeeking(false);
                  }}
                  onPointerCancel={(e) => {
                    e.stopPropagation();
                    setIsTricksPeeking(false);
                  }}
                  onMouseDown={() => setIsTricksPeeking(true)}
                  onMouseUp={() => setIsTricksPeeking(false)}
                  onMouseLeave={() => setIsTricksPeeking(false)}
                >
                  FLIP CARDS
                </button>
              </div>
              <button className="close-overlay" onClick={() => {
                setIsTricksPeeking(false);
                setIsViewingTricks(false);
              }}>✕</button>
            </div>
            <div className="tricks-grid">
              {(me?.wonTricks || []).length === 0 ? (
                <div className="no-tricks-msg">No tricks won this round.</div>
              ) : (
                (me?.wonTricks || []).slice().reverse().map((trick, tIdx) => (
                  <div key={trick.map(pc => pc.card?.id).filter(Boolean).join('-') || tIdx} className="trick-row">
                    <div className="trick-cards-stack">
                      {trick.map((pc, cIdx) => (
                        <div key={cIdx} className="mini-card-container">
                          {/* Re-using your exact hand-card structure so your CSS works here too */}
                          <div className={`card-wrapper static-card ${isTricksPeeking ? "peeking" : ""}`}>
                            <div className="card-inner">
                              <div className="card-face card-front">
                                <img
                                  src={getSvgPath(pc.card.image)}
                                  alt="front"
                                  className="card-img"
                                />
                              </div>
                              <div className="card-face card-back">
                                <img src={getBackPath(pc.card.deckColor || 'blue')} alt="back" className="card-img" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="tricks-live-panel">
            <h2>Live Board</h2>
            <div className="played-cards-area tricks-live-board" aria-label="Live board">
              {(gameState.playedCards || []).map((pc, idx) => (
                pc?.card && (
                  <div
                    key={pc.card.id || idx}
                    className="card played-card"
                    style={{
                      left: `${20 + (idx * 64)}px`,
                      zIndex: idx
                    }}
                  >
                    <img
                      src={getSvgPath(pc.card.image)}
                      alt="played"
                      className="card-img"
                    />
                  </div>
                )
              ))}
            </div>
          </div>
          </div>
          {IS_NATIVE_PHONE_APP && (
            <>
              <button
                type="button"
                className="phone-tricks-close"
                aria-label="Close"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsTricksPeeking(false);
                  setIsViewingTricks(false);
                }}
              >
                ✕
              </button>
              <button
                type="button"
                className={`peek-btn phone-tricks-flip ${isTricksPeeking ? "active" : ""}`}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsTricksPeeking(true);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsTricksPeeking(false);
                }}
                onTouchCancel={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsTricksPeeking(false);
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setIsTricksPeeking(true);
                }}
                onPointerUp={(e) => {
                  e.stopPropagation();
                  setIsTricksPeeking(false);
                }}
                onPointerCancel={(e) => {
                  e.stopPropagation();
                  setIsTricksPeeking(false);
                }}
                onMouseDown={() => setIsTricksPeeking(true)}
                onMouseUp={() => setIsTricksPeeking(false)}
                onMouseLeave={() => setIsTricksPeeking(false)}
              >
                FLIP CARDS
              </button>
            </>
          )}
        </div>
      )}
      {winnerData && (
        <div className="full-screen-overlay winner-screen">
          {finalScoreboardData && (
            <div className="winner-scoreboard-panel">
              <div className="scoreboard end-scoreboard">
                <h3>Scoreboard</h3>
                <div className="score-table-wrapper" ref={endScoreboardScrollRef}>
                  <table className="score-table">
                    <thead>
                      <tr>
                        <th className="round-label-cell">RND</th>
                        {finalScoreboardData.players.map((p) => (
                          <th key={p.id} title={p.name}>{p.name}</th>
                        ))}
                        <th className="scroller-gutter"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ROUND_SEQUENCE.map((rVal, rIdx) => (
                        <tr key={rIdx} className={rIdx === 13 ? "halfway-divider" : ""}>
                          <td className="round-label-cell">{rVal}</td>
                          {finalScoreboardData.players.map((p) => {
                            const val = finalScoreboardData.scores[p.id]?.[rIdx];
                            const isDefiniteValue = val !== undefined && val !== null;
                            const cellClasses = val < 0 ? "neg-score" : val > 0 ? "pos-score" : "";
                            return (
                              <td key={p.id} className={cellClasses}>
                                {isDefiniteValue ? (val > 0 ? `+${val}` : val) : "-"}
                              </td>
                            );
                          })}
                          <td className="scroller-gutter"></td>
                        </tr>
                      ))}
                      <tr className="total-row">
                        <td className="round-label-cell">TOT</td>
                        {finalScoreboardData.players.map((p) => {
                          const playerScores = finalScoreboardData.scores[p.id] || [];
                          const total = playerScores.reduce((acc, curr) => acc + (curr || 0), 0);
                          const cellClasses = total < 0 ? "neg-score" : total > 0 ? "pos-score" : "";
                          return (
                            <td key={p.id} className={cellClasses}>{total}</td>
                          );
                        })}
                        <td className="scroller-gutter"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <button className="download-scoreboard-btn" type="button" onClick={downloadFinalScoreboard}>DOWNLOAD</button>
            </div>
          )}
          <div className="winner-content">
            <h1 className="game-over-title">GAME OVER</h1>
            
            <div className="rankings-list">
              {winnerData.map((player) => {
                let rankClass = "rank-normal";
                let medal = "";
                if (player.rank === 1) { rankClass = "rank-gold"; medal = "🥇"; }
                else if (player.rank === 2) { rankClass = "rank-silver"; medal = "🥈"; }
                else if (player.rank === 3) { rankClass = "rank-bronze"; medal = "🥉"; }

                return (
                  <div key={player.id} className={`rank-item ${rankClass}`}>
                    <span className="rank-number">
                      {medal || `${player.rank}${getOrdinal(player.rank)}`}
                    </span>
                    <img src={player.avatar} alt="avatar" className="rank-avatar" draggable={false} />
                    <span className="rank-name">{player.name}</span>
                    <span className="rank-score">{player.totalScore} pts</span>
                  </div>
                );
              })}
            </div>

            <button 
              className={`play-again-btn ${!canReload ? "disabled" : ""}`}
              disabled={!canReload}
              onClick={() => window.location.reload()}
            >
              {canReload ? "PLAY AGAIN" : "WAITING FOR SERVER..."/* WAITING FOR SERVER... */} 
            </button>
          </div>
        </div>
      )}
      {gameTerminationMessage && (
        <div className="termination-overlay">
          <div className="termination-popup">
            <div className="termination-message">{gameTerminationMessage}</div>
            <button className="play-again-btn" onClick={() => window.location.reload()}>
              PLAY AGAIN
            </button>
          </div>
        </div>
      )}
      {draggingCard && (
        <div
          className="dragging-card-shadow"
          style={{
            left: `${draggingCard.x - draggingCard.offsetX}px`,
            top: `${draggingCard.y - draggingCard.offsetY}px`,
            width: `${draggingCard.width}px`,
            height: `${draggingCard.height}px`
          }}
        />
      )}
      {draggingCard && (
        <div
          className="dragging-card-overlay"
          style={{
            left: `${draggingCard.x - draggingCard.offsetX}px`,
            top: `${draggingCard.y - draggingCard.offsetY}px`,
            width: `${draggingCard.width}px`,
            height: `${draggingCard.height}px`
          }}
        >
          <img
            src={hideCards && gameState.currentRound !== 1 ? HIDDEN_CARD_PATH : (gameState.currentRound === 1 ? getBackPath(draggingCard.card.deckColor) : getSvgPath(draggingCard.card.image))}
            alt="dragging"
            className="card-img"
          />
        </div>
      )}
{/* ... Place this right before the last </div> in your return block ... */} 
      {gameState.debug && (
        <button 
          className="debug-fill-seats-btn"
          onClick={() => setFillSeatsPopupOpen(true)}
          style={{
            position: 'fixed',
            bottom: '40px', /* was 20px */
            left: '20px',
            padding: '12px 24px',
            background: '#ff0000',
            color: 'white',
            zIndex: 99999,
            cursor: 'pointer',
            border: '3px solid white',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '14px'
          }}
        >
          FILL SEATS
        </button>
      )}
      {gameState.debug && fillSeatsPopupOpen && (
        <div className="debug-fill-popup-overlay">
          <div className="popup debug-fill-popup" role="dialog" aria-modal="true" aria-labelledby="debug-fill-popup-title">
            <button
              type="button"
              className="debug-fill-popup-close"
              aria-label="Close"
              onClick={() => setFillSeatsPopupOpen(false)}
            >
              ×
            </button>
            <h2 id="debug-fill-popup-title">How many bots?</h2>
            <div className="debug-fill-number-grid">
              {Array.from({ length: 7 }, (_, index) => index + 1).map((botCount) => (
                <button
                  type="button"
                  key={botCount}
                  className="debug-fill-number-btn"
                  onClick={() => {
                    socket.emit("fillSeats", botCount);
                    console.log(`Requested ${botCount} Fill Seats`);
                    setFillSeatsPopupOpen(false);
                  }}
                >
                  {botCount}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {gameState.debug && (
        <button 
          className="debug-force-skip-btn"
          onClick={() => {
            socket.emit("forceNextRound");
            console.log("Requested Force Skip");
          }}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            padding: '12px 24px',
            background: '#ff0000',
            color: 'white',
            zIndex: 99999,
            cursor: 'pointer',
            border: '3px solid white',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '14px'
          }}
        >
          FORCE SKIP ROUND
        </button>
      )}
      {connectionStatus && !winnerData && !takeoverMessage && !gameTerminationMessage && (
        <div className={`connection-status-overlay connection-status-overlay--${connectionStatus}`} role="alert" aria-live="assertive">
          <div className="connection-status-card">
            <div className="connection-status-indicator" aria-hidden="true" />
            <h2>
              {connectionStatus === "offline" && "No internet connection"}
              {connectionStatus === "disconnected" && "Connection lost"}
              {connectionStatus === "restoring" && "Connection restored"}
              {connectionStatus === "rejoining" && "Rejoining game…"}
              {connectionStatus === "checking" && "Checking connection…"}
              {connectionStatus === "failed" && "Could not rejoin game"}
            </h2>
            {connectionStatus !== "rejoining" && (
              <p>
                {connectionStatus === "offline" && "Waiting for your connection to return…"}
                {connectionStatus === "disconnected" && "Trying to reconnect…"}
                {connectionStatus === "restoring" && "Reconnecting to the game…"}
                {connectionStatus === "checking" && "Waiting for the server to respond…"}
                {connectionStatus === "failed" && "Please refresh the page to try again."}
              </p>
            )}
            {connectionStatus === "failed" && (
              <button type="button" className="connection-status-refresh" onClick={() => window.location.reload()}>
                Refresh
              </button>
            )}
          </div>
          {IS_NATIVE_PHONE_APP && (
            <button type="button" className="phone-refresh-btn" aria-label="Refresh page" onClick={() => window.location.reload()}>
              <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19.9 14a8 8 0 1 1-3.4-8.4" />
                <path d="M20 4v5h-5" />
              </svg>
            </button>
          )}
        </div>
      )}
      {seatExpired && (
        <div className="seat-expired-overlay" role="alertdialog" aria-modal="true" aria-labelledby="seat-expired-title" aria-describedby="seat-expired-message">
          <div className="seat-expired-card">
            <div className="seat-expired-symbol" aria-hidden="true">⌛</div>
            <h2 id="seat-expired-title">Removed from game</h2>
            <p id="seat-expired-message">You have lost connection for too long, therefore you've been removed from this game.</p>
            <button type="button" className="seat-expired-return" onClick={() => window.location.reload()}>Return to home</button>
          </div>
          {IS_NATIVE_PHONE_APP && (
            <button type="button" className="phone-refresh-btn" aria-label="Refresh page" onClick={() => window.location.reload()}>
              <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19.9 14a8 8 0 1 1-3.4-8.4" />
                <path d="M20 4v5h-5" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default App;

// ==================== CONNECTION DIAGNOSTIC LOGGER START ====================

if (typeof window !== "undefined") {
  let activeGameId = null;
  let awaitingConfig = false;
  let heartbeatInterval = null;
  let observerInterval = null;
  let connectionUiObserver = null;
  let lastConnectionUi = null;
  let observedEngine = null;
  let lastEnginePingAt = null;
  let enginePingCount = 0;
  let lastHeartbeatAt = null;
  let lastGameStateUpdateAt = null;
  let gameStateUpdateCount = 0;
  let pendingDiagnosticReports = [];
  let pendingCardUpdates = [];
  let lastReceivedCardCount = null;
  let observedPlayedCardsArea = null;
  let lastCommittedCardCount = null;

  const countCommittedPlayedCards = (area) =>
    area.querySelectorAll(":scope > .played-card").length;

  const getDiagnosticIdentity = () => {
    try {
      return {
        profileId: window.localStorage.getItem(PROFILE_STORAGE_KEY) || null,
        wasSeated: window.localStorage.getItem("rikiWasSeated") === "1"
      };
    } catch {
      return { profileId: null, wasSeated: null };
    }
  };

  const createDiagnosticReport = (event, details = {}) => ({
    event,
    gameId: activeGameId,
    clientTimestamp: new Date().toISOString(),
    socketId: socket.id || null,
    socketConnected: socket.connected,
    transport: socket.io.engine?.transport?.name || null,
    browserOnline: window.navigator.onLine,
    visibilityState: window.document.visibilityState,
    lastGameStateUpdateAt,
    lastGameStateUpdateAgeMs: lastGameStateUpdateAt
      ? Date.now() - Date.parse(lastGameStateUpdateAt)
      : null,
    gameStateUpdateCount,
    lastEnginePingAgeMs: lastEnginePingAt ? Date.now() - lastEnginePingAt : null,
    enginePingCount,
    ...getDiagnosticIdentity(),
    ...details
  });

  const sendDiagnosticReport = (event, details = {}) => {
    if (!activeGameId) return;
    const report = createDiagnosticReport(event, details);

    if (socket.connected && !awaitingConfig && window.navigator.onLine) {
      socket.emit("connectionDiagnostic", report);
      return;
    }

    pendingDiagnosticReports.push(report);
    pendingDiagnosticReports = pendingDiagnosticReports.slice(-50);
  };

  const flushPendingDiagnosticReports = () => {
    if (!socket.connected || awaitingConfig || !window.navigator.onLine || pendingDiagnosticReports.length === 0) return;

    const reports = pendingDiagnosticReports;
    pendingDiagnosticReports = [];
    reports.forEach(report => {
      socket.emit("connectionDiagnostic", {
        ...report,
        delayedDelivery: true,
        deliverySocketId: socket.id || null,
        deliveredAt: new Date().toISOString()
      });
    });
  };

  const playedCardsObserver = new MutationObserver(() => {
    const area = observedPlayedCardsArea;
    if (!area || !window.document.contains(area)) return;

    const committedCardCount = countCommittedPlayedCards(area);
    if (committedCardCount === lastCommittedCardCount) return;

    const previousCardCount = lastCommittedCardCount;
    lastCommittedCardCount = committedCardCount;
    const commitTime = window.performance.now();
    const animatedCardCount = area.querySelectorAll(
      ":scope > .played-card-flight"
    ).length;
    const matchingIndex = pendingCardUpdates
      .map(update => update.playedCardCount)
      .lastIndexOf(committedCardCount);
    const matchedUpdates = matchingIndex >= 0
      ? pendingCardUpdates.splice(0, matchingIndex + 1)
      : [];
    const mostRecentUpdate = matchedUpdates[matchedUpdates.length - 1];

    window.requestAnimationFrame(() => {
      sendDiagnosticReport("CLIENT_PLAYED_CARDS_FRAME", {
        previousCardCount,
        committedCardCount,
        animatedCardCount,
        cardCountAtFrame: window.document.contains(area)
          ? countCommittedPlayedCards(area)
          : null,
        receivedCardUpdates: matchedUpdates.map(update => ({
          gameStateUpdateCount: update.gameStateUpdateCount,
          currentRound: update.currentRound,
          playedCardCount: update.playedCardCount,
          receivedAt: update.receivedAt
        })),
        domCommitDelayMs: mostRecentUpdate
          ? Math.round(commitTime - mostRecentUpdate.receivedAtPerformance)
          : null,
        frameDelayMs: mostRecentUpdate
          ? Math.round(window.performance.now() - mostRecentUpdate.receivedAtPerformance)
          : null,
        unmatchedDomChange: !mostRecentUpdate
      });
    });
  });

  const attachPlayedCardsObserver = () => {
    const area = window.document.querySelector(
      ".center-container > .played-cards-area"
    );
    if (!area || area === observedPlayedCardsArea) return;

    playedCardsObserver.disconnect();
    observedPlayedCardsArea = area;
    lastCommittedCardCount = countCommittedPlayedCards(area);
    playedCardsObserver.observe(area, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"]
    });
    sendDiagnosticReport("CLIENT_PLAYED_CARDS_OBSERVER_ATTACHED", {
      committedCardCount: lastCommittedCardCount
    });
  };

  const readConnectionUi = () => {
    const overlay = window.document.querySelector(".connection-status-overlay");
    return {
      status: [...(overlay?.classList || [])]
        .find(name => name.startsWith("connection-status-overlay--"))
        ?.replace("connection-status-overlay--", "") || null,
      stallWarning: !!window.document.querySelector(".connection-stall-warning"),
      seatExpired: !!window.document.querySelector(".seat-expired-overlay")
    };
  };

  const reportConnectionUiChange = () => {
    const ui = readConnectionUi();
    const signature = JSON.stringify(ui);
    if (signature === lastConnectionUi) return;
    lastConnectionUi = signature;
    sendDiagnosticReport("CLIENT_CONNECTION_UI_CHANGED", ui);
  };

  const onEnginePing = () => {
    const now = Date.now();
    const intervalMs = lastEnginePingAt ? now - lastEnginePingAt : null;
    lastEnginePingAt = now;
    enginePingCount += 1;
    sendDiagnosticReport("CLIENT_ENGINE_PING", { intervalMs });
  };
  const watchEngine = () => {
    const engine = socket.io.engine;
    if (!engine || engine === observedEngine) return;
    observedEngine?.off("ping", onEnginePing);
    observedEngine = engine;
    observedEngine.on("ping", onEnginePing);
  };

  const stopDiagnosticSession = () => {
    activeGameId = null;
    awaitingConfig = false;
    pendingDiagnosticReports = [];
    pendingCardUpdates = [];
    playedCardsObserver.disconnect();
    observedPlayedCardsArea = null;
    connectionUiObserver?.disconnect();
    connectionUiObserver = null;
    observedEngine?.off("ping", onEnginePing);
    observedEngine = null;
    window.clearInterval(heartbeatInterval);
    window.clearInterval(observerInterval);
    heartbeatInterval = null;
    observerInterval = null;
    lastConnectionUi = null;
  };

  socket.on("connectionDiagnosticConfig", ({ enabled, gameId } = {}) => {
    if (!enabled || !gameId) {
      stopDiagnosticSession();
      return;
    }
    if (activeGameId === gameId) {
      awaitingConfig = false;
      flushPendingDiagnosticReports();
      return;
    }

    stopDiagnosticSession();
    activeGameId = gameId;
    lastGameStateUpdateAt = null;
    gameStateUpdateCount = 0;
    lastReceivedCardCount = null;
    lastCommittedCardCount = null;
    lastEnginePingAt = null;
    enginePingCount = 0;
    lastHeartbeatAt = Date.now();
    watchEngine();
    attachPlayedCardsObserver();
    observerInterval = window.setInterval(attachPlayedCardsObserver, 1000);
    connectionUiObserver = new MutationObserver(reportConnectionUiChange);
    connectionUiObserver.observe(window.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"]
    });
    reportConnectionUiChange();
    heartbeatInterval = window.setInterval(() => {
      const now = Date.now();
      sendDiagnosticReport("CLIENT_HEARTBEAT", {
        eventLoopDelayMs: Math.max(0, now - lastHeartbeatAt - 5000),
        pendingReportCount: pendingDiagnosticReports.length,
        connectionUi: readConnectionUi()
      });
      lastHeartbeatAt = now;
    }, 5000);
    sendDiagnosticReport("CLIENT_GAME_LOG_JOINED");
  });

  socket.on("connect", () => {
    if (activeGameId) awaitingConfig = true;
    if (activeGameId) watchEngine();
    sendDiagnosticReport("CLIENT_SOCKET_CONNECTED");
  });

  socket.on("disconnect", (reason, details) => {
    sendDiagnosticReport("CLIENT_SOCKET_DISCONNECTED", {
      reason,
      message: details?.message || null,
      description: details?.description || null
    });
  });

  socket.on("connect_error", (error) => {
    sendDiagnosticReport("CLIENT_CONNECT_ERROR", {
      message: error?.message || String(error),
      description: error?.description || null
    });
  });

  socket.io.on("reconnect_attempt", (attempt) => {
    sendDiagnosticReport("CLIENT_RECONNECT_ATTEMPT", { attempt });
  });

  socket.io.on("reconnect", (attempt) => {
    sendDiagnosticReport("CLIENT_RECONNECTED", { attempt });
  });

  socket.io.on("reconnect_error", (error) => {
    sendDiagnosticReport("CLIENT_RECONNECT_ERROR", {
      message: error?.message || String(error)
    });
  });

  socket.io.on("reconnect_failed", () => {
    sendDiagnosticReport("CLIENT_RECONNECT_FAILED");
  });

  socket.onAnyOutgoing?.((event, payload) => {
    if (!["submitBid", "playCard", "reconnectRequest", "profileLogin", "takeSeat"].includes(event)) return;
    sendDiagnosticReport("CLIENT_ACTION_SENT", {
      action: event,
      value: event === "submitBid" || event === "playCard" ? payload : null,
      hasReconnectToken: !!payload?.reconnectToken,
      hasRecoveryKey: !!payload?.recoveryKey
    });
  });

  socket.on("seatConfirmed", ({ wasDisconnected } = {}) => {
    sendDiagnosticReport("CLIENT_SEAT_CONFIRMED", { wasDisconnected: !!wasDisconnected });
  });
  socket.on("seatExpired", () => sendDiagnosticReport("CLIENT_SEAT_EXPIRED"));
  socket.on("reconnectFailed", () => sendDiagnosticReport("CLIENT_RECONNECT_REJECTED"));
  socket.on("profileTakenOver", () => sendDiagnosticReport("CLIENT_PROFILE_TAKEN_OVER"));
  socket.on("gameTerminatedLastPlayer", () => sendDiagnosticReport("CLIENT_GAME_TERMINATED"));
  socket.on("playError", message => sendDiagnosticReport("CLIENT_PLAY_ERROR", { message }));
  socket.on("playersUpdate", players => {
    if (!activeGameId) return;
    sendDiagnosticReport("CLIENT_PLAYERS_RECEIVED", {
      seats: players.map((player, seatIndex) => player && ({
        seatIndex,
        playerId: player.playerId,
        socketId: player.socketId,
        disconnected: player.disconnected,
        ghost: player.ghost,
        reconnectTimeLeft: player.reconnectTimeLeft
      })).filter(Boolean)
    });
  });

  socket.on("gameStateUpdate", (state) => {
    if (!activeGameId) return;
    lastGameStateUpdateAt = new Date().toISOString();
    gameStateUpdateCount += 1;
    const playedCardCount = Array.isArray(state?.playedCards)
      ? state.playedCards.length
      : null;

    if (playedCardCount !== lastReceivedCardCount) {
      pendingCardUpdates.push({
        gameStateUpdateCount,
        currentRound: state?.currentRound ?? null,
        playedCardCount,
        receivedAt: lastGameStateUpdateAt,
        receivedAtPerformance: window.performance.now()
      });
      pendingCardUpdates = pendingCardUpdates.slice(-20);
      lastReceivedCardCount = playedCardCount;
    }

    sendDiagnosticReport("CLIENT_GAME_STATE_RECEIVED", {
      gameStarted: state?.gameStarted ?? null,
      currentRound: state?.currentRound ?? null,
      turnIndex: state?.turnIndex ?? null,
      callerIndex: state?.callerIndex ?? null,
      playedCardCount
    });
  });

  window.addEventListener("online", () => {
    flushPendingDiagnosticReports();
    sendDiagnosticReport("BROWSER_ONLINE");
  });
  window.addEventListener("offline", () => sendDiagnosticReport("BROWSER_OFFLINE"));
  window.addEventListener("pageshow", event => {
    sendDiagnosticReport("PAGE_SHOWN", { restoredFromCache: event.persisted });
  });
  window.addEventListener("pagehide", event => {
    sendDiagnosticReport("PAGE_HIDDEN", { enteringCache: event.persisted });
  });
  window.document.addEventListener("visibilitychange", () => {
    sendDiagnosticReport("PAGE_VISIBILITY_CHANGED");
  });

}

// ===================== CONNECTION DIAGNOSTIC LOGGER END =====================
